/**
 * Comprehensive SDK Test Page
 * Tests all available APIs from the SDK:
 * - FiClient (trading operations)
 * - InfoClient (market data)
 * - Utility functions (satoshi conversion)
 */

import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  InfoClient,
  toSatoshi,
  fromSatoshi,
  toSatoshiUint64,
  isValidSatoshi,
  toSatoshiBatch,
  fromSatoshiBatch,
  SATOSHI_SCALE,
  mormSigner,
} from "../sdk";
import { SubscriptionProvider, useSubscription } from "../context/SubscriptionContext";
import { useFiClient } from "../hooks/useFiClient";
import { int64ToHex, toNumberFromSatoshi } from "../sdk/utils/math";
import { formatPriceFromSatoshi, formatLeverage } from "../sdk/utils/display";
import {
  CURRENCY_MIN_FRACTION_DIGITS,
  CURRENCY_MAX_FRACTION_DIGITS,
  DEFAULT_MNEMONIC,
  BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS,
  BUCKET_EQUITY_TEST_AMOUNT_USDC,
  BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC,
  BUCKET_EQUITY_TEST_BANK_TRANSFER_AMOUNT_USDC,
} from "../constants";
import { useMarkets } from "../hooks/useMarkets";
import type { Market } from "../store/types";
import type { RawBucket } from "../sdk";
import { useOnboard } from "../hooks/useOnboard";
import { useBuckets } from "../hooks/useBuckets";
import { useNonce } from "../hooks/useNonce";
import { useNonceStore } from "../store";
import { DEFAULT_BASE_URL, DEFAULT_DEVICE_ID, DEFAULT_WS_URL } from "../constants";
import {
  RunAllTestsButton,
  OnboardButton,
  UtilityTestButton,
  InfoClientTestButton,
  WalletSpecificTestButton,
  FiClientTestButton,
  FiClientOrderTestButton,
  BucketEquityTestButton,
  ClearResultsButton,
  DismissErrorButton,
} from "./sdk_test_page/SDKTestPageButtons";
import { TestResultItem, TestResult } from "./sdk_test_page/TestResultItem";
import { SectionHeader } from "./sdk_test_page/SectionHeader";
import "./sdk_test_page/sdktest.css";

interface TestSection {
  name: string;
  results: TestResult[];
}

function SDKTestPageContent() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [marketIndex, setMarketIndex] = useState<string>("");
  const [sessionToken, setSessionToken] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);
  const [sections, setSections] = useState<Record<string, TestSection>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [isAuthTestRunning, setIsAuthTestRunning] = useState(false);
  const [isUtilityTestRunning, setIsUtilityTestRunning] = useState(false);
  const [isInfoClientTestRunning, setIsInfoClientTestRunning] = useState(false);
  const [isWalletSpecificTestRunning, setIsWalletSpecificTestRunning] = useState(false);
  const [isFiClientTestRunning, setIsFiClientTestRunning] = useState(false);
  const [isFiClientOrderTestRunning, setIsFiClientOrderTestRunning] = useState(false);
  const [isBucketEquityTestRunning, setIsBucketEquityTestRunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [onchainNonce, setOnchainNonce] = useState<number | null>(null);
  const [onchainNonceError, setOnchainNonceError] = useState<string | null>(null);

  const infoClientRef = useRef<InfoClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);
  const fetchedMarketsRef = useRef<Market[]>([]);

  /** Single source: first active market ID, or first market in list. */
  function getFirstMarketId(list: Market[]): string {
    if (!list || list.length === 0) return "";
    const m = list.find((x) => x.active) ?? list[0];
    return m.index ?? m.marketIndex ?? "";
  }

  // Use onboarding hook as single source of truth for account information (must run before useFiClient so mnemonic is defined)
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    loading: onboardLoading,
    error: onboardError,
    onboard: performOnboard,
    clearError: clearOnboardError,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  const { client: exchangeClient, isReady: exchangeReady } = useFiClient({
    baseURL,
    mnemonic: mnemonic ?? "",
  });

  const {
    nextNonce: syncedNextNonce,
    loading: nonceLoading,
    error: nonceError,
    queryNonce,
  } = useNonce(baseURL, walletAddress ?? undefined);

  const localNonceState = useNonceStore((s) =>
    walletAddress ? s.getNonceState(walletAddress) : undefined,
  );

  // Fetch onchain nonce when wallet or baseURL changes
  useEffect(() => {
    if (!walletAddress || !baseURL.trim()) {
      setOnchainNonce(null);
      setOnchainNonceError(null);
      return;
    }
    let cancelled = false;
    const infoClient = new InfoClient({ baseURL });
    infoClient
      .getNonceStatus(walletAddress, false)
      .then((res) => {
        if (cancelled) return;
        const raw =
          (res as { next_nonce?: number | string; nextNonce?: number | string }).next_nonce ??
          (res as { next_nonce?: number | string; nextNonce?: number | string }).nextNonce;
        const n =
          typeof raw === "string" ? parseInt(raw, 10) : typeof raw === "number" ? raw : null;
        setOnchainNonce(n);
        setOnchainNonceError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setOnchainNonce(null);
          setOnchainNonceError(err?.message ?? "Failed to fetch onchain nonce");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [walletAddress, baseURL]);

  // Sync nonce (queryNonce) when wallet becomes available so synced next is shown
  useEffect(() => {
    if (!walletAddress || !baseURL.trim()) return;
    queryNonce(false).catch(() => {});
  }, [walletAddress, baseURL]);

  // Use markets hook to manage market data retention
  const { markets, fetchMarkets } = useMarkets({
    baseURL,
    sessionToken,
    autoFetchMarkets: true, // Load markets on mount so config has default market and FiClient can run without InfoClient first
  });

  // Initialize mnemonic with default value if not set
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(DEFAULT_MNEMONIC );
    }
  }, [mnemonic, setOnboardMnemonic]);

  // Update mnemonic ref when mnemonic changes
  const mnemonicRef = useRef<string>(mnemonic || "");
  useEffect(() => {
    mnemonicRef.current = mnemonic || "";
  }, [mnemonic]);

  // Use buckets hook (uses context from SubscriptionProvider; no subscriptionClient prop)
  const {
    buckets: hookBuckets,
    loading: bucketsLoading,
    error: bucketsError,
    refresh: refreshBuckets,
  } = useBuckets({
    baseURL,
    authToken: sessionToken || authToken,
    walletAddress: walletAddress,
    refreshInterval: 0, // Disable auto-refresh for test page
    useWebSocket: false, // Disable WebSocket for test page
    marketId: "0", // Default to user-global market
  });

  // Sync auth and wallet to SubscriptionProvider so context client stays in sync
  const { setAuthToken: setSubscriptionAuthToken, setWalletAddress: setSubscriptionWalletAddress } =
    useSubscription();
  useEffect(() => {
    setSubscriptionAuthToken(sessionToken || authToken || undefined);
  }, [sessionToken, authToken, setSubscriptionAuthToken]);
  useEffect(() => {
    setSubscriptionWalletAddress(walletAddress || undefined);
  }, [walletAddress, setSubscriptionWalletAddress]);

  // Sync onboard auth token to local state when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setAuthToken(onboardAuthToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Sync authToken state to localStorage whenever it changes
  useEffect(() => {
    if (authToken && authToken.trim() !== "") {
      localStorage.setItem("authToken", authToken);
    }
  }, [authToken]);

  // Show test completion toast when tests finish
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // When isRunning changes from true to false, tests just completed
    if (prevIsRunningRef.current === true && isRunning === false) {
      // Wait a bit for state to update, then calculate results
      setTimeout(() => {
        const allResults = Object.values(sections).flatMap((s) => s.results);
        const successCount = allResults.filter((r) => r.success).length;
        const totalCount = allResults.length;
        const failureCount = totalCount - successCount;

        if (totalCount > 0) {
          if (failureCount === 0) {
            toast.success("All tests passed", {
              description: `${totalCount} test(s) completed successfully.`,
              duration: 5000,
            });
          } else {
            toast.warning("Tests completed with failures", {
              description: `${successCount}/${totalCount} tests passed. ${failureCount} test(s) failed.`,
              duration: 6000,
            });
          }
        }
      }, 100);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, sections]);

  // Load authToken from localStorage on mount
  useEffect(() => {
    const storedAuthToken = localStorage.getItem("authToken");
    if (storedAuthToken && !authToken) {
      setAuthToken(storedAuthToken);
    }
  }, []);

  const defaultMarketId =
    markets.length > 0 ? getFirstMarketId(markets) || null : null;
  const effectiveMarketId = defaultMarketId ?? marketIndex;

  // Update marketIndex state when default changes (for UI consistency)
  useEffect(() => {
    if (defaultMarketId && marketIndex !== defaultMarketId) {
      setMarketIndex(defaultMarketId);
    }
  }, [defaultMarketId]);

  // Update refs when values change
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  const addResult = (section: string, result: TestResult) => {
    setSections((prev) => {
      const sectionData = prev[section] || { name: section, results: [] };
      return {
        ...prev,
        [section]: {
          ...sectionData,
          results: [...sectionData.results, result],
        },
      };
    });
  };

  const clearResults = () => {
    setSections({});
    setExpandedSections(new Set());
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  // Auto-expand sections when they first get results
  useEffect(() => {
    const newSections = Object.keys(sections).filter(
      (name) => sections[name].results.length > 0,
    );
    if (newSections.length > 0) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        newSections.forEach((name) => next.add(name));
        return next;
      });
    }
  }, [sections]);

  // Utility Functions Tests
  const testUtilities = () => {
    const section = "Utilities";
    addResult(section, {
      success: true,
      message: "Testing utility functions...",
      expected: null,
      actual: null,
      timestamp: Date.now(),
    });

    try {
      // Test toSatoshi
      const satoshi1 = toSatoshi(1.0);
      addResult(section, {
        success: satoshi1 === "100000000",
        message: `toSatoshi(1.0) = ${satoshi1}`,
        expected: "100000000",
        actual: satoshi1,
        timestamp: Date.now(),
      });

      const satoshi2 = toSatoshi("1.5");
      addResult(section, {
        success: satoshi2 === "150000000",
        message: `toSatoshi("1.5") = ${satoshi2}`,
        expected: "150000000",
        actual: satoshi2,
        timestamp: Date.now(),
      });

      // Test fromSatoshi
      const decimal1 = fromSatoshi("100000000");
      addResult(section, {
        success: decimal1 === 1.0,
        message: `fromSatoshi("100000000") = ${decimal1}`,
        expected: 1.0,
        actual: decimal1,
        timestamp: Date.now(),
      });

      // Test fromSatoshi with large value (precision test)
      const largeSatoshi = "64999999999986700000000";
      const decimalLarge = fromSatoshi(largeSatoshi);
      const expectedLarge = 649999999999867;
      const isPrecise = decimalLarge === expectedLarge;
      addResult(section, {
        success: isPrecise,
        message: `fromSatoshi("${largeSatoshi}") = ${decimalLarge}`,
        expected: expectedLarge,
        actual: {
          got: decimalLarge,
          difference: Math.abs(decimalLarge - expectedLarge),
          isPrecise,
        },
        timestamp: Date.now(),
      });

      // Test toNumberFromSatoshi with large value
      const toNumberLargeSatoshi = "64999999999986700000000";
      const toNumberDecimalLarge = toNumberFromSatoshi(toNumberLargeSatoshi);
      const toNumberExpectedLarge = 649999999999867;
      const toNumberIsPrecise = toNumberDecimalLarge === toNumberExpectedLarge;
      addResult(section, {
        success: toNumberIsPrecise,
        message: `toNumberFromSatoshi("${toNumberLargeSatoshi}") = ${toNumberDecimalLarge}`,
        expected: toNumberExpectedLarge,
        actual: {
          got: toNumberDecimalLarge,
          difference: Math.abs(toNumberDecimalLarge - toNumberExpectedLarge),
          isPrecise: toNumberIsPrecise,
        },
        timestamp: Date.now(),
      });

      // Test toSatoshiUint64
      const uint64 = toSatoshiUint64(1.0);
      addResult(section, {
        success: uint64 === 100000000,
        message: `toSatoshiUint64(1.0) = ${uint64}`,
        expected: 100000000,
        actual: uint64,
        timestamp: Date.now(),
      });

      // Test isValidSatoshi
      const valid1 = isValidSatoshi("100000000");
      const valid2 = isValidSatoshi("abc");
      addResult(section, {
        success: valid1 && !valid2,
        message: `isValidSatoshi tests: "100000000"=${valid1}, "abc"=${valid2}`,
        expected: { valid1: true, valid2: false },
        actual: { valid1, valid2 },
        timestamp: Date.now(),
      });

      // Test batch functions
      const batchInput = [1.0, 1.5, 2.0];
      const batchSatoshi = toSatoshiBatch(batchInput);
      const batchDecimal = fromSatoshiBatch(batchSatoshi);
      addResult(section, {
        success: batchDecimal.length === 3,
        message: `Batch conversion: ${batchInput.length} items`,
        expected: { length: 3, input: batchInput },
        actual: { satoshi: batchSatoshi, decimal: batchDecimal },
        timestamp: Date.now(),
      });

      // Test SATOSHI_SCALE constant
      addResult(section, {
        success: SATOSHI_SCALE === 100000000,
        message: `SATOSHI_SCALE = ${SATOSHI_SCALE}`,
        expected: 100000000,
        actual: SATOSHI_SCALE,
        timestamp: Date.now(),
      });

      // Test int64ToHex with large value
      const largeValue = "64999999999986700000000";
      const hexResult = int64ToHex(largeValue);
      addResult(section, {
        success: hexResult.startsWith("0x"),
        message: `int64ToHex("${largeValue}") = ${hexResult}`,
        expected: { prefix: "0x", input: largeValue },
        actual: { output: hexResult, isValidHex: hexResult.startsWith("0x") },
        timestamp: Date.now(),
      });

      // Test formatPriceFromSatoshi with large value
      const largePriceValue = "64999999999986700000000";
      const formattedPrice = formatPriceFromSatoshi(largePriceValue);
      addResult(section, {
        success: formattedPrice !== "--" && formattedPrice.length > 0,
        message: `formatPriceFromSatoshi("${largePriceValue}") = ${formattedPrice}`,
        expected: { nonEmpty: true, input: largePriceValue },
        actual: { output: formattedPrice, isValid: formattedPrice !== "--" && formattedPrice.length > 0 },
        timestamp: Date.now(),
      });

      // Test formatLeverage with string value
      const leverageValue = "86755123000";
      const formattedLeverage = formatLeverage(leverageValue);
      addResult(section, {
        success: formattedLeverage !== "--" && formattedLeverage.length > 0,
        message: `formatLeverage("${leverageValue}") = ${formattedLeverage}`,
        expected: { nonEmpty: true, input: leverageValue },
        actual: { output: formattedLeverage, isValid: formattedLeverage !== "--" && formattedLeverage.length > 0 },
        timestamp: Date.now(),
      });

      // Test toLocaleString with currency format options
      const number_x = 649999999999867;
      const currencyFormatOptions = {
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        maximumFractionDigits: CURRENCY_MAX_FRACTION_DIGITS,
      };
      const formattedCurrency = number_x.toLocaleString("en-US", currencyFormatOptions);
      addResult(section, {
        success: formattedCurrency.length > 0 && formattedCurrency.includes("649"),
        message: `number_x.toLocaleString("en-US", currencyFormatOptions) = ${formattedCurrency}`,
        expected: { nonEmpty: true, contains: "649", input: number_x },
        actual: { output: formattedCurrency, currencyFormatOptions, isValid: formattedCurrency.length > 0 },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Utility test error",
        expected: null,
        actual: null,
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Authentication Test Section
  const testAuthentication = async (section: string = "Authentication") => {
    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Authentication skipped: Wallet address required",
        expected: "wallet address",
        actual: null,
        timestamp: Date.now(),
      });
      return;
    }

    // Check if mormSigner is available for address generation (still needed)
    // Note: Actual signing now uses ethers.js, but we need WASM for address derivation
    if (!mormSigner.isReady()) {
      addResult(section, {
        success: false,
        message:
          "Authentication failed: mormSigner not loaded. Please initialize mormSigner first for address generation.",
        expected: "mormSigner ready",
        actual: null,
        timestamp: Date.now(),
      });
      return;
    }

    try {
      addResult(section, {
        success: true,
        message:
          "Starting authentication (onboarding) with ethers.js EIP-712 signing...",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });

      // Use the refactored onboarding function
      // This uses ethers.js for EIP-712 signing (via signing/onboard.ts)
      // onboardAndGetAuthToken handles: nonce fetching, message building, signing, and HTTP request
      const { onboardAndGetAuthToken } = await import("../sdk/utils/onboard");

      addResult(section, {
        success: true,
        message:
          "Calling onboardAndGetAuthToken (handles nonce, EIP-712 signing with ethers.js, and API request)...",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });

      if (!mnemonic) {
        throw new Error("Mnemonic is required for authentication");
      }

      const result = await onboardAndGetAuthToken(mnemonic, baseURLRef.current);

      if (!result.success || !result.authToken) {
        addResult(section, {
          success: true,
          message:
            "Onboard API is not ready but it doesn't hurt. Skipping onboard step.",
          expected: "API ready or skip",
          actual: { warning: true, detail: result.error },
          timestamp: Date.now(),
        });
        return;
      }

      // Success! Store token (the hook's useEffect will sync onboardAuthToken)
      setAuthToken(result.authToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", result.authToken);

      addResult(section, {
        success: true,
        message: `AUTHENTICATION SUCCESSFUL!`,
        expected: "auth token and wallet",
        actual: {
          walletAddress,
          authToken: result.authToken.substring(0, 32) + "...",
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      const msg = error.message || String(error);
      addResult(section, {
        success: false,
        message: `Authentication failed: ${msg}`,
        expected: "successful auth",
        actual: null,
        error: msg,
        timestamp: Date.now(),
      });
    }
  };
  // Wallet Specific Tests (requires wallet address, session token is optional)
  const testWalletSpecific = async () => {
    const section = "Wallet Specific";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    // Wait for wallet address to be available (with timeout)
    addResult(section, {
      success: true,
      message: "Waiting for wallet address to be available...",
      expected: null,
      actual: null,
      timestamp: Date.now(),
    });

    const maxWaitTime = 5000; // 5 seconds timeout
    const pollInterval = 200; // Check every 200ms
    let waitedTime = 0;

    // Wait for wallet address to be available
    while (!walletAddress || walletAddress.trim() === "") {
      if (waitedTime >= maxWaitTime) {
        addResult(section, {
          success: false,
          message: "Timeout: Wallet address not available after 5 seconds",
          expected: "wallet address within 5s",
          actual: null,
          error:
            "Wallet address is required for wallet-specific tests. Please ensure mnemonic is provided.",
          timestamp: Date.now(),
        });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      waitedTime += pollInterval;
      // Note: walletAddress is a closure variable that captures the state value
      // If the state updates during this wait, the closure won't reflect it
      // However, the wallet address is typically generated synchronously from mnemonic
      // So this wait is mainly for cases where generation is still in progress
    }

    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address is required for wallet-specific tests",
        expected: "wallet address",
        actual: null,
        error: "Wallet address not available",
        timestamp: Date.now(),
      });
      return;
    }

    addResult(section, {
      success: true,
      message: `Wallet address available: ${walletAddress.substring(0, 10)}...`,
      expected: null,
      actual: null,
      timestamp: Date.now(),
    });

    try {
      // Test getBalance
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "MORM" at address ${walletAddress.substring(0, 10)}...`,
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      try {
        const balance = await infoClientRef.current.getBalance(
          walletAddress,
          "MORM",
        );
        const balanceValue =
          balance != null && balance.balance != null ? balance.balance : "N/A";
        const balanceStr =
          typeof balanceValue === "string"
            ? balanceValue
            : JSON.stringify(balanceValue);
        addResult(section, {
          success: !!balance,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: MORM | Balance: ${balanceStr} (raw satoshi format)`,
          expected: "balance object (satoshi format)",
          actual: {
            balanceStr,
            asset: "MORM",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          raw_data: balance,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "MORM"`,
          expected: "balance response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBalance for USDC
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "USDC" at address ${walletAddress.substring(0, 10)}...`,
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      try {
        const balanceUSDC = await infoClientRef.current.getBalance(
          walletAddress,
          "USDC",
        );
        const balanceValueUSDC =
          balanceUSDC != null && balanceUSDC.balance != null
            ? balanceUSDC.balance
            : "N/A";
        const balanceStrUSDC =
          typeof balanceValueUSDC === "string"
            ? balanceValueUSDC
            : JSON.stringify(balanceValueUSDC);
        addResult(section, {
          success: !!balanceUSDC,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: USDC | Balance: ${balanceStrUSDC} (raw satoshi format)`,
          expected: "balance object (satoshi format)",
          actual: {
            balanceStr: balanceStrUSDC,
            asset: "USDC",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          raw_data: balanceUSDC,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "USDC"`,
          expected: "balance response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBalanceDecimal
      try {
        const balanceDecimal = await infoClientRef.current.getBalanceDecimal(
          walletAddress,
          "USDC",
        );
        addResult(section, {
          success: !!balanceDecimal,
          message: "getBalanceDecimal() - GET /bank/v1/balance/{address}",
          expected: "balance in decimal format",
          actual: balanceDecimal,
          raw_data: balanceDecimal,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalanceDecimal() failed",
          expected: "balance response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBalances
      try {
        const balances = await infoClientRef.current.getBalances(walletAddress);
        addResult(section, {
          success: !!balances,
          message: "getBalances() - GET /bank/v1/balances/{address}",
          expected: "balances array/object",
          actual: balances,
          raw_data: balances,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalances() failed",
          expected: "balances response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getPositions
      try {
        const positions =
          await infoClientRef.current.getPositions(walletAddress);
        const positionsList = Array.isArray(positions) ? positions : [];
        addResult(section, {
          success: Array.isArray(positions),
          message: `getPositions() - GET /bucket/v1/address/{address}/positions returned ${positionsList.length} positions`,
          expected: "positions array",
          actual: { count: positionsList.length, sample: positionsList.slice(0, 2) },
          raw_data: positions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getPositions() failed",
          expected: "positions response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrders
      let ordersArray: any[] = [];
      try {
        const ordersResponse =
          await infoClientRef.current.getOrders(walletAddress);
        const res = ordersResponse;
        ordersArray = res && res.orders ? res.orders : [];
        const totalCount = res != null ? res.totalCount : undefined;
        const isValidResponse =
          res &&
          (Array.isArray(ordersArray) || res.totalCount !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getOrders() - GET /clob/v1/address/orders?address={address} returned ${ordersArray.length} orders (totalCount: ${totalCount != null ? totalCount : "N/A"})`,
          expected: "orders response with orders array or totalCount",
          actual: {
            orders: ordersArray.slice(0, 3),
            totalCount,
            timestamp: res != null ? res.timestamp : undefined,
          },
          raw_data: res,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrders() failed",
          expected: "orders response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrder by ID (only if getOrders returned at least one order)
      if (Array.isArray(ordersArray) && ordersArray.length > 0) {
        const firstOrder = ordersArray[0];
        const orderId =
          (firstOrder != null && firstOrder.orderId) || firstOrder.id;
        // Use the address from the order itself, or fall back to the wallet address
        const orderAddress =
          (firstOrder != null && firstOrder.address) || walletAddress;
        if (orderId) {
          addResult(section, {
            success: true,
            message: `Testing getOrder() for order ID: ${orderId.substring(0, 16)}... (from getOrders result)`,
            expected: null,
            actual: null,
            timestamp: Date.now(),
          });
          try {
            const orderById = await infoClientRef.current.getOrder(orderId, orderAddress);
            addResult(section, {
              success: !!orderById,
              message: `getOrder() - GET /clob/v1/order/{orderID}?address={address} | Order ID: ${orderId.substring(0, 16)}...`,
              expected: "order object",
              actual: { orderId, address: orderAddress, source: "First order from getOrders() result" },
              raw_data: orderById,
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: `getOrder() failed for order ID: ${orderId.substring(0, 16)}...`,
              expected: "order response",
              actual: null,
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: false,
            message:
              "getOrder() test skipped: No valid order ID found in getOrders() result",
            expected: "orderId or id on first order",
            actual: {
              order: firstOrder,
              availableFields: Object.keys(firstOrder != null ? firstOrder : {}),
            },
            error: "Order ID not found in order object",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "getOrder() test skipped: No orders available from getOrders() (need at least 1 order)",
          expected: null,
          actual: { ordersCount: ordersArray.length, note: "getOrder() test requires at least one order from getOrders()" },
          timestamp: Date.now(),
        });
      }

      // Test getOrdersByAddress (order history for address)
      try {
        const orderHistoryResponse =
          await infoClientRef.current.getOrdersByAddress(walletAddress);
        const orderHistoryArray = Array.isArray(orderHistoryResponse)
          ? orderHistoryResponse
          : (orderHistoryResponse && orderHistoryResponse.orders) ||
            [];
        const isValidResponse =
          orderHistoryResponse &&
          (Array.isArray(orderHistoryArray) ||
            (orderHistoryResponse.orders !== undefined));
        addResult(section, {
          success: isValidResponse,
          message: `getOrdersByAddress() - GET /clob/v1/address/{address}/orders (QueryOrdersByAddress) returned ${orderHistoryArray.length} orders`,
          expected: "orders array or response with orders",
          actual: orderHistoryArray.slice(0, 3),
          raw_data: orderHistoryResponse,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrdersByAddress() failed",
          expected: "order history response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getTradeHistory
      try {
        const tradeHistoryResponse =
          await infoClientRef.current.getTradeHistory(walletAddress);
        const tradeHistoryArray = Array.isArray(tradeHistoryResponse)
          ? tradeHistoryResponse
          : [];
        const isValidResponse = Array.isArray(tradeHistoryArray);
        addResult(section, {
          success: isValidResponse,
          message: `getTradeHistory() - GET /clob/v1/address/trades?address={address} returned ${tradeHistoryArray.length} trades`,
          expected: "trades array",
          actual: tradeHistoryArray.slice(0, 3),
          raw_data: tradeHistoryResponse,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getTradeHistory() failed",
          expected: "trade history response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBuckets (direct SDK call)
      try {
        const buckets = await infoClientRef.current.getBuckets(walletAddress);
        const bucketList = Array.isArray(buckets) ? buckets : [];
        addResult(section, {
          success: bucketList.length > 0 || buckets !== undefined,
          message: `getBuckets() - GET /bucket/v1/address/{address}/buckets returned ${bucketList.length} buckets`,
          expected: "buckets array",
          actual: bucketList.slice(0, 3),
          raw_data: buckets,
          timestamp: Date.now(),
        });

        if (bucketList.length > 0) {
          const firstBucket = bucketList[0];
          const bucketIdRaw =
            firstBucket != null && firstBucket.bucketId != null
              ? firstBucket.bucketId
              : null;
          const bucketIdStr =
            bucketIdRaw != null ? String(bucketIdRaw) : "";
          if (bucketIdStr) {
            try {
              const bucket =
                await infoClientRef.current.getBucket(bucketIdStr);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} (${bucketIdStr.substring(0, 16)}...)`,
                expected: "bucket object",
                actual: bucket != null ? { bucketId: bucketIdStr } : null,
                raw_data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket(${bucketIdStr.substring(0, 16)}...) failed`,
                expected: "bucket response",
                actual: null,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBuckets() failed",
          expected: "buckets response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test useBuckets hook
      addResult(section, {
        success: true,
        message: "Testing useBuckets hook...",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      try {
        // Refresh buckets using the hook
        await refreshBuckets();
        addResult(section, {
          success: !bucketsError && !bucketsLoading,
          message: `useBuckets hook: ${hookBuckets.length} buckets loaded`,
          expected: "buckets loaded without error",
          actual: {
            buckets: hookBuckets.slice(0, 3),
            loading: bucketsLoading,
            error: bucketsError,
            totalBuckets: hookBuckets.length,
          },
          timestamp: Date.now(),
        });

        // If hook has buckets, test getBucket with first one
        if (hookBuckets.length > 0) {
          const firstHookBucket = hookBuckets[0];
          const rawId =
            firstHookBucket != null && firstHookBucket.bucketId != null
              ? firstHookBucket.bucketId
              : null;
          const hookBucketId = rawId != null ? String(rawId) : "";
          if (hookBucketId) {
            try {
              const bucket =
                await infoClientRef.current.getBucket(hookBucketId);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} via hook bucket ID (${hookBucketId.substring(0, 16)}...)`,
                expected: "bucket object",
                actual: bucket != null ? { bucketId: hookBucketId } : null,
                raw_data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket() via hook bucket ID failed`,
                expected: "bucket response",
                actual: null,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "useBuckets hook test failed",
          expected: null,
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Wallet Specific test error",
        expected: null,
        actual: null,
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  const testInfoClient = async () => {
    const section = "InfoClient";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    try {
      // Test getMarkets - use hook to retain data
      addResult(section, {
        success: true,
        message: "Testing getMarkets()...",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });

      const marketsData = await fetchMarkets();
      const marketsList = Array.isArray(marketsData) ? marketsData : [];
      fetchedMarketsRef.current = marketsList;
      const marketCount = marketsList.length;

      const firstMarket =
        marketCount > 0
          ? (marketsList.find((m: Market) => m.active) ?? marketsList[0])
          : null;
      const firstMarketId = firstMarket
        ? (firstMarket.index ?? firstMarket.marketIndex ?? null)
        : null;

      const marketDisplayLabel =
        firstMarket != null
          ? (firstMarket.moniker ?? firstMarket.symbol ?? "auto-selected")
          : "";

      addResult(section, {
        success: marketCount > 0,
        message: `getMarkets() - GET /market/v1/all returned ${marketCount} markets${firstMarketId ? ` | Using market ID: ${firstMarketId} (${marketDisplayLabel})` : ""}`,
        expected: "markets array",
        actual: {
          count: marketCount,
          defaultMarketId: firstMarketId,
          defaultMarket:
            firstMarket != null
              ? {
                  index: firstMarket.index,
                  marketIndex: firstMarket.marketIndex,
                  moniker: firstMarket.moniker,
                  symbol: firstMarket.symbol,
                  active: firstMarket.active,
                  status: firstMarket.status,
                }
              : null,
          markets: marketsList.slice(0, 5),
        },
        raw_data: marketsData,
        timestamp: Date.now(),
      });

      // Automatically set the first market ID for demo purposes (use exact string)
      if (firstMarketId) {
        setMarketIndex(firstMarketId);

        const marketDisplayName =
          firstMarket != null
            ? (firstMarket.moniker ??
               firstMarket.symbol ??
               (firstMarket.baseAsset && firstMarket.quoteAsset
                 ? `${firstMarket.baseAsset}-${firstMarket.quoteAsset}`
                 : "Unknown"))
            : "Unknown";

        addResult(section, {
          success: true,
          message: `Markets loaded: ${marketCount} | Auto-selected: ${marketDisplayName} (ID: ${firstMarketId.substring(0, 10)}...)`,
          expected: null,
          actual: {
            selectedMarketId: firstMarketId,
            count: marketCount,
            market: firstMarket,
          },
          timestamp: Date.now(),
        });

        // Critical: Wait for React state to propagate before next tests
        addResult(section, {
          success: true,
          message: `Waiting 1.5s for market selection to take effect...`,
          timestamp: Date.now(),
        });

        await new Promise((resolve) => setTimeout(resolve, 1500)); // 1.5 second delay

        addResult(section, {
          success: true,
          message: `Market ID ready: ${firstMarketId}`,
          expected: null,
          actual: { marketId: firstMarketId },
          timestamp: Date.now(),
        });

        // Optional: Confirm in UI
        addResult(section, {
          success: true,
          message: `Proceeding with market: ${marketDisplayName}`,
          expected: null,
          actual: null,
          timestamp: Date.now(),
        });
      }

      // Test market-specific endpoints (only if market index is available)
      // After the delay, React state should be updated, but use the index we just set to be safe
      const currentMarketIndex = firstMarketId || effectiveMarketId || marketIndex;
      if (currentMarketIndex) {
        // Test QueryOrderBookSnapshot (with decimal conversion)
        addResult(section, {
          success: true,
          message: `Testing QueryOrderBookSnapshot(${currentMarketIndex}, convertToDecimal=true)...`,
          timestamp: Date.now(),
        });
        try {
          const orderbook = await infoClientRef.current.getOrderbook(
            currentMarketIndex,
            true,
          );
          const bids = orderbook && orderbook.bids ? orderbook.bids.slice(0, 3) : [];
          const asks = orderbook && orderbook.asks ? orderbook.asks.slice(0, 3) : [];
          addResult(section, {
            success: !!orderbook && (orderbook.bids != null || orderbook.asks != null),
            message:
              "QueryOrderBookSnapshot - GET /clob/v1/orderbook/{marketIndex} with decimal conversion",
            expected: "orderbook snapshot with bids/asks, sequenceId, checksum",
            actual: { bids, asks, sequenceId: orderbook?.sequenceId, checksum: orderbook?.checksum },
            raw_data: orderbook,
            timestamp: Date.now(),
          });

          const orderbookRaw = await infoClientRef.current.getOrderbook(
            currentMarketIndex,
            false,
          );
          const bidsRaw =
            orderbookRaw && orderbookRaw.bids ? orderbookRaw.bids.slice(0, 2) : [];
          const asksRaw =
            orderbookRaw && orderbookRaw.asks ? orderbookRaw.asks.slice(0, 2) : [];
          addResult(section, {
            success:
              !!orderbookRaw &&
              (orderbookRaw.bids != null || orderbookRaw.asks != null),
            message:
              "QueryOrderBookSnapshot - GET /clob/v1/orderbook/{marketIndex} raw satoshi format",
            expected: "orderbook snapshot raw (bids/asks in satoshi strings)",
            actual: { bids: bidsRaw, asks: asksRaw },
            raw_data: orderbookRaw,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "QueryOrderBookSnapshot - GET /clob/v1/orderbook/{marketIndex} failed",
            expected: "orderbook response",
            actual: null,
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Test getTicker
        addResult(section, {
          success: true,
          message: `Testing getTicker(${currentMarketIndex})...`,
          timestamp: Date.now(),
        });
        try {
          const ticker = await infoClientRef.current.getTicker(currentMarketIndex);
          addResult(section, {
            success: !!ticker,
            message: "getTicker() - GET /market/v1/market/{marketId}/stats",
            expected: "ticker/stats object",
            actual: ticker,
            raw_data: ticker,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getTicker() - GET /market/v1/market/{marketId}/stats failed",
            expected: "ticker response",
            actual: null,
            error: error.message,
            timestamp: Date.now(),
          });
        }

      } else {
        addResult(section, {
          success: false,
          message: "Skipping market-specific tests: No market ID available",
          error: "Please ensure markets are loaded and a market is selected",
          timestamp: Date.now(),
        });
      }

      // Test router/v1 endpoints (public, no auth required)
      addResult(section, {
        success: true,
        message: "Testing router/v1 endpoints...",
        timestamp: Date.now(),
      });

      // Test getSupportedActions
      try {
        const supportedActions =
          await infoClientRef.current.getSupportedActions();
        const actions =
          supportedActions != null ? supportedActions.actions : undefined;
        addResult(section, {
          success:
            Array.isArray(actions) || actions !== undefined,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions",
          expected: "supported actions array/object",
          actual: supportedActions,
          raw_data: supportedActions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions failed",
          expected: "supported actions response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test validateAction
      try {
        const validateResult =
          await infoClientRef.current.validateAction("clob::submit_order");
        addResult(section, {
          success: validateResult != null,
          message:
            'validateAction() - GET /router/v1/tx/validate-action ("clob::submit_order")',
          expected: "validation result",
          actual: validateResult,
          raw_data: validateResult,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "validateAction() - GET /router/v1/tx/validate-action failed",
          expected: "validate response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthSummary
      try {
        const healthSummary = await infoClientRef.current.getHealthSummary();
        addResult(section, {
          success: !!healthSummary,
          message: "getHealthSummary() - GET /router/v1/health/summary",
          expected: "health summary object",
          actual: healthSummary,
          raw_data: healthSummary,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthSummary() - GET /router/v1/health/summary failed",
          expected: "health summary response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthDaemons
      try {
        const healthDaemons = await infoClientRef.current.getHealthDaemons();
        addResult(section, {
          success: !!healthDaemons,
          message: "getHealthDaemons() - GET /router/v1/health/daemons",
          expected: "health daemons object",
          actual: healthDaemons,
          raw_data: healthDaemons,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthDaemons() - GET /router/v1/health/daemons failed",
          expected: "health daemons response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getServerTime
      try {
        const serverTime = await infoClientRef.current.getServerTime();
        addResult(section, {
          success:
            !!serverTime &&
            (serverTime.serverTime !== undefined || serverTime !== undefined),
          message: "getServerTime() - GET /router/v1/time",
          expected: "server time object",
          actual: serverTime,
          raw_data: serverTime,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getServerTime() - GET /router/v1/time failed",
          expected: "time response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsTPS
      try {
        const metricsTPS = await infoClientRef.current.getMetricsTPS();
        addResult(section, {
          success: !!metricsTPS,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps",
          expected: "TPS metrics object",
          actual: metricsTPS,
          raw_data: metricsTPS,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps failed",
          expected: "metrics response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsLatency
      try {
        const metricsLatency = await infoClientRef.current.getMetricsLatency();
        addResult(section, {
          success: !!metricsLatency,
          message: "getMetricsLatency() - GET /router/v1/metrics/latency",
          expected: "latency metrics object",
          actual: metricsLatency,
          raw_data: metricsLatency,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getMetricsLatency() - GET /router/v1/metrics/latency failed",
          expected: "latency response",
          actual: null,
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "InfoClient test error",
        expected: null,
        actual: null,
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // FiClient Tests
  // the dex testing flow and the basic verification of the fi client
  const testFiClient = async () => {
    const section = "DEX Test";
    if (!mnemonic) {
      addResult(section, {
        success: false,
        message: "Mnemonic required for FiClient tests",
        expected: "mnemonic",
        actual: null,
        error: "No mnemonic provided",
        timestamp: Date.now(),
      });
      return;
    }
    if (!exchangeClient || !exchangeReady) {
      addResult(section, {
        success: false,
        message: "FiClient not ready",
        expected: "FiClient initialized",
        actual: null,
        error: "Set mnemonic and base URL, then wait for FiClient to initialize.",
        timestamp: Date.now(),
      });
      return;
    }

    // Read market ID from page state (UI input) or from loaded markets
    // Try in order: 1) marketIndex from state, 2) effectiveMarketId (has fallback), 3) from fetched ref, 4) from markets array
    let currentMarketId =
      marketIndex || effectiveMarketId || getFirstMarketId(fetchedMarketsRef.current) || "";
    let defaultMarket: Market | null = null;

    // If still empty, try to get from fetched markets ref (loaded in previous sections, immediately available)
    if (!currentMarketId || currentMarketId.trim() === "") {
      if (fetchedMarketsRef.current && fetchedMarketsRef.current.length > 0) {
        // Find first active market, or first market in list (same logic as testInfoClient)
        const firstMarket =
          fetchedMarketsRef.current.find((m: Market) => m.active) ??
          fetchedMarketsRef.current[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.index ?? firstMarket.marketIndex ?? "";
          defaultMarket = firstMarket;
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from fetched markets ref: ${currentMarketId.substring(0, 16)}...`,
            expected: null,
            actual: {
              source: "fetchedMarketsRef",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker ?? undefined,
                symbol: firstMarket.symbol ?? undefined,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      } else if (markets && markets.length > 0) {
        const firstMarket =
          markets.find((m: Market) => m.active) ?? markets[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.index ?? firstMarket.marketIndex ?? "";
          defaultMarket = firstMarket;
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from markets state: ${currentMarketId.substring(0, 16)}...`,
            expected: null,
            actual: {
              source: "markets state",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker ?? undefined,
                symbol: firstMarket.symbol ?? undefined,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      }

      // Last resort: fetch markets now so FiClient can run without running InfoClient first
      if (!currentMarketId || currentMarketId.trim() === "") {
        addResult(section, {
          success: true,
          message: "No market ID available; fetching markets now...",
          expected: null,
          actual: null,
          timestamp: Date.now(),
        });
        try {
          const marketsData = await fetchMarkets();
          const list = Array.isArray(marketsData) ? marketsData : [];
          fetchedMarketsRef.current = list;
          const firstMarket =
            list.length > 0
              ? (list.find((m: Market) => m.active) ?? list[0])
              : null;
          const resolvedId = firstMarket
            ? (firstMarket.index ?? firstMarket.marketIndex ?? "")
            : "";
          if (resolvedId) {
            setMarketIndex(resolvedId);
            currentMarketId = resolvedId;
            addResult(section, {
              success: true,
              message: `Using market from fetch: ${resolvedId.substring(0, 16)}...`,
              expected: null,
              actual: {
                source: "fetchMarkets in FiClient",
                marketId: resolvedId,
                count: list.length,
              },
              timestamp: Date.now(),
            });
          }
        } catch (err: unknown) {
          const msg = err != null && typeof (err as Error).message === "string"
            ? (err as Error).message
            : String(err);
          addResult(section, {
            success: false,
            message: "Failed to fetch markets for market ID",
            expected: "market ID from fetch",
            actual: null,
            error: msg,
            timestamp: Date.now(),
          });
        }
      }
    }

    // Final check - if still empty, show error with debug info
    if (!currentMarketId || currentMarketId.trim() === "") {
      addResult(section, {
        success: false,
        message: "Market ID required from page or loaded markets",
        error: `Please enter or select a market ID in the configuration section, or ensure markets are loaded first. Debug: marketIndex="${marketIndex || "(empty)"}", effectiveMarketId="${effectiveMarketId || "(empty)"}", fromFetchedRef="${getFirstMarketId(fetchedMarketsRef.current) || "(empty)"}", fetchedMarketsRef.length=${fetchedMarketsRef.current.length}, markets.length=${markets.length}`,
        expected: null,
        actual: {
          marketIndex: marketIndex || "(empty)",
          effectiveMarketId: effectiveMarketId || "(empty)",
          defaultMarketId: defaultMarketId || "(empty)",
          fromFetchedRef: getFirstMarketId(fetchedMarketsRef.current) || "(empty)",
          fetchedMarketsRefCount: fetchedMarketsRef.current.length,
          marketsCount: markets.length,
          marketsAvailable:
            markets.length > 0 || fetchedMarketsRef.current.length > 0,
        },
        timestamp: Date.now(),
      });
      return;
    }

    // Resolve defaultMarket from loaded markets if not set (e.g. when currentMarketId came from page state)
    if (!defaultMarket) {
      const match = (m: Market) =>
        (m.index ?? m.marketIndex ?? "") === currentMarketId;
      const fromRef =
        fetchedMarketsRef.current && fetchedMarketsRef.current.find(match);
      const fromState = markets && markets.find(match);
      defaultMarket = fromRef ?? fromState ?? null;
    }

    // Read wallet address from page state (UI input)
    const currentWalletAddress = walletAddress || "";
    if (!currentWalletAddress || currentWalletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address required from page",
        error:
          "Wallet address should be generated from mnemonic or entered manually. Please ensure mnemonic is valid.",
        timestamp: Date.now(),
      });
      return;
    }

    // Determine source of market ID for logging
    const marketIdSource = marketIndex
      ? "page state (marketIndex)"
      : effectiveMarketId
        ? "page state (effectiveMarketId)"
        : "loaded markets array";

    addResult(section, {
      success: true,
      message: `Using market ID from ${marketIdSource}: ${currentMarketId.substring(0, 16)}...`,
      expected: null,
      actual: {
        marketId: currentMarketId,
        source: marketIdSource,
      },
      timestamp: Date.now(),
    });

    addResult(section, {
      success: true,
      message: `Using wallet address from page: ${currentWalletAddress.substring(0, 10)}...`,
      expected: null,
      actual: { walletAddress: currentWalletAddress },
      timestamp: Date.now(),
    });

    // Log signing tokens if available
    if (sessionToken || authToken) {
      addResult(section, {
        success: true,
        message: `Using signing tokens: ${sessionToken ? "Session Token" : ""} ${authToken ? "Auth Token" : ""}`,
        expected: null,
        actual: {
          hasSessionToken: !!sessionToken,
          hasAuthToken: !!authToken,
        },
        timestamp: Date.now(),
      });
    }

    try {
      addResult(section, {
        success: true,
        message: "Using FiClient (already initialized).",
        timestamp: Date.now(),
      });
      const walletAddr = exchangeClient.getWalletAddress();

      // Verify wallet address matches the one from page
      if (walletAddr.toLowerCase() !== currentWalletAddress.toLowerCase()) {
        addResult(section, {
          success: false,
          message: "Wallet address mismatch",
          error: `FiClient wallet (${walletAddr}) does not match page wallet (${currentWalletAddress})`,
          timestamp: Date.now(),
        });
        return;
      }

      addResult(section, {
        success: true,
        message: `FiClient initialized successfully (Wallet: ${walletAddr.substring(0, 10)}...)`,
        expected: null,
        actual: {
          walletAddress: walletAddr,
          marketId: currentMarketId,
          verified: true,
        },
        timestamp: Date.now(),
      });

      // Use marketId as string directly (no conversion needed)
      const marketIdString = currentMarketId;
      addResult(section, {
        success: true,
        message: `Using market ID as string: ${marketIdString}`,
        expected: null,
        actual: { marketId: marketIdString },
        timestamp: Date.now(),
      });

      // --- Exchange Client Test Flow (tradingAutomation.md: 9 steps) ---
      const WORKING_CAPITAL = 500;
      const HEALTH_RATE_SAFE = 1.5;
      const HEALTH_RATE_DANGER = 1;
      const HEALTH_RATE_REVERT = 1.2;
      const IMBALANCE_THRESHOLD = 1.5;
      const DEFAULT_LEVERAGE = 10;

      if (!infoClientRef.current) {
        infoClientRef.current = new InfoClient({
          baseURL: baseURLRef.current,
        });
      }
      if (sessionToken && infoClientRef.current) {
        infoClientRef.current.setSessionToken(sessionToken);
      }
      const infoClient = infoClientRef.current;

      // Step 1: Refresh nonce from API before any write transactions
      addResult(section, {
        success: true,
        message: "Step 1: Refreshing nonce token from API before tests...",
        timestamp: Date.now(),
      });
      try {
        await exchangeClient.refreshNonceFromServer();
        let nonceNumber: number | null = null;
        try {
          const status = await infoClient.getNonceStatus(walletAddr, false);
          const raw = (status as { next_nonce?: number | string; nextNonce?: number | string }).next_nonce
            ?? (status as { next_nonce?: number | string; nextNonce?: number | string }).nextNonce;
          nonceNumber = typeof raw === "string" ? parseInt(raw, 10) : typeof raw === "number" ? raw : null;
        } catch {
          // nonceNumber stays null; still report success for refresh
        }
        addResult(section, {
          success: true,
          message: nonceNumber != null
            ? `Nonce refreshed from API (router/v1/nonce/status); next nonce: ${nonceNumber}; ready for write transactions`
            : "Nonce refreshed from API (router/v1/nonce/status); ready for write transactions",
          expected: null,
          actual: {
            nonce: nonceNumber,
            note: "FiClient nonce synced with server before balance/transfer/placeOrder",
          },
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: "Failed to refresh nonce from API (Step 1)",
          error: e?.message,
          timestamp: Date.now(),
        });
      }

      // Step 2: Initialization
      addResult(section, {
        success: true,
        message: "Step 2: Initialization — WORKING_CAPITAL=500 USD, USDC base, default market from config",
        expected: null,
        actual: { workingCapital: WORKING_CAPITAL, defaultSymbol: defaultMarket?.symbol ?? marketIdString },
        timestamp: Date.now(),
      });

      // Step 3: Balance checks — MORM bank, USDC bank, per-bucket USDC, SUM_BUCKET_USDC
      addResult(section, {
        success: true,
        message: "Step 3: Balance checks — fetch bank MORM, bank USDC, list bucket USDC",
        timestamp: Date.now(),
      });
      let mormBankUsd = 0;
      let usdcBankUsd = 0;
      try {
        const mormBal = await infoClient.getBalanceDecimal(walletAddr, "MORM");
        mormBankUsd = mormBal?.available ?? mormBal?.balance ?? 0;
        if (mormBankUsd <= 0) {
          addResult(section, { success: true, message: "Insufficient MORM in bank (proceed with caution)", expected: null, actual: { mormBankUsd }, timestamp: Date.now() });
        }
        const usdcBal = await infoClient.getBalanceDecimal(walletAddr, "USDC");
        usdcBankUsd = usdcBal?.available ?? usdcBal?.balance ?? 0;
        addResult(section, {
          success: true,
          message: `Bank MORM: ${mormBankUsd.toFixed(2)}; Bank USDC: ${usdcBankUsd.toFixed(2)}`,
          expected: null,
          actual: { mormBankUsd, usdcBankUsd },
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, { success: false, message: "Balance fetch failed", error: e?.message, timestamp: Date.now() });
      }

      const buckets = await infoClient.getBuckets(walletAddr).catch(() => []);
      const bucketsList: RawBucket[] = Array.isArray(buckets) ? buckets : [];
      type BucketRow = { bucketId: string; usdc: number; equity: number };
      const bucketRows: BucketRow[] = [];
      for (const b of bucketsList) {
        const bid = b.bucketId ?? "";
        if (!bid) continue;
        try {
          const bucketResp: RawBucket = await infoClient.getBucket(bid);
          const am = bucketResp.availableMargin ?? "0";
          const eq = bucketResp.totalEquity ?? "0";
          bucketRows.push({
            bucketId: bid,
            usdc: fromSatoshi(am),
            equity: fromSatoshi(eq),
          });
        } catch {
          bucketRows.push({ bucketId: bid, usdc: 0, equity: 0 });
        }
      }
      let SUM_BUCKET_USDC = bucketRows.reduce((s, x) => s + x.usdc, 0);
      addResult(section, {
        success: true,
        message: `Display: Bank MORM ${mormBankUsd.toFixed(2)}, Bank USDC ${usdcBankUsd.toFixed(2)}, SUM_BUCKET_USDC ${SUM_BUCKET_USDC.toFixed(2)}`,
        expected: null,
        actual: { sumBucketUsdc: SUM_BUCKET_USDC, perBucket: bucketRows },
        timestamp: Date.now(),
      });

      // Step 4: Fund sufficiency and transfer logic
      if (SUM_BUCKET_USDC < WORKING_CAPITAL && usdcBankUsd < WORKING_CAPITAL) {
        addResult(section, {
          success: false,
          message: "Insufficient funds in both bank and buckets. Stop testing flow.",
          expected: null,
          actual: { sumBucketUsdc: SUM_BUCKET_USDC, usdcBankUsd, workingCapital: WORKING_CAPITAL },
          timestamp: Date.now(),
        });
        addResult(section, { success: true, message: "FiClient tests completed (stopped at fund sufficiency)", expected: null, actual: { stoppedAt: "fundSufficiency" }, timestamp: Date.now() });
      } else if (SUM_BUCKET_USDC < WORKING_CAPITAL && usdcBankUsd >= WORKING_CAPITAL) {
        if (bucketRows.length === 0) {
          addResult(section, { success: false, message: "No buckets available for transfer. Stop testing flow.", expected: null, actual: null, timestamp: Date.now() });
          addResult(section, { success: true, message: "FiClient tests completed (stopped at transfer)", expected: null, actual: { stoppedAt: "noBuckets" }, timestamp: Date.now() });
        } else {
          const targetRow = bucketRows.reduce((a, b) => (a.equity <= b.equity ? a : b));
          const transferAmount = Math.min(
            (WORKING_CAPITAL - SUM_BUCKET_USDC) * 5,
            usdcBankUsd,
          );
          addResult(section, {
            success: true,
            message: `Transfer from bank USDC to target bucket (lowest equity): ${transferAmount.toFixed(2)}`,
            expected: null,
          actual: { targetBucketId: targetRow.bucketId, transferAmount },
            timestamp: Date.now(),
          });
          try {
            if (transferAmount > 0 && exchangeClient.extractToBucket) {
              const transferResult = await exchangeClient.extractToBucket({
                bucketId: targetRow.bucketId,
                assetId: "USDC",
                amount: transferAmount,
              });
              const txAccepted = transferResult?.success === true;
              const txHash = transferResult?.txhash ?? "";
              if (!txAccepted) {
                const errMsg = (transferResult as { errorMessage?: string; message?: string })?.errorMessage ?? (transferResult as { message?: string })?.message ?? "";
                addResult(section, {
                  success: false,
                  message: "Transfer from bank to bucket: server did not accept (success=false)",
                  expected: null,
                  actual: { error: errMsg, txhash: txHash || undefined },
                  raw_data: transferResult,
                  timestamp: Date.now(),
                });
              } else {
                if (txHash) {
                  addResult(section, {
                    success: true,
                    message: `Bank→bucket tx accepted, txhash: ${txHash}`,
                    expected: null,
          actual: { txhash: txHash, sumBucketUsdc: SUM_BUCKET_USDC },
                    timestamp: Date.now(),
                  });
                }
                // Step: Re-query specific bucket balance and bank balance, then update sumBucketUsdc (one test step)
                try {
                  const [bucketAfter, bankUsdcAfter] = await Promise.all([
                    infoClient.getBucket(targetRow.bucketId),
                    infoClient.getBalanceDecimal(walletAddr, "USDC"),
                  ]);
                  targetRow.usdc = fromSatoshi(bucketAfter.availableMargin ?? "0");
                  targetRow.equity = fromSatoshi(bucketAfter.totalEquity ?? "0");
                  const bankUsdcAfterValue = bankUsdcAfter?.available ?? bankUsdcAfter?.balance ?? 0;
                  SUM_BUCKET_USDC = bucketRows.reduce((s, x) => s + x.usdc, 0);
                  addResult(section, {
                    success: true,
                    message: "After transfer: query bucket balance + bank balance, update SUM_BUCKET_USDC",
                    expected: null,
          actual: {
                      targetBucketId: targetRow.bucketId,
                      bucketUsdc: targetRow.usdc,
                      bankUsdc: bankUsdcAfterValue,
                      sumBucketUsdc: SUM_BUCKET_USDC,
                      ...(txHash ? { txhash: txHash } : {}),
                    },
                    timestamp: Date.now(),
                  });
                } catch (e: any) {
                  addResult(section, {
                    success: false,
                    message: "Re-query bucket and bank balance after transfer failed",
                    error: e?.message,
                    timestamp: Date.now(),
                  });
                }
                addResult(section, {
                  success: true,
                  message: `After transfer: SUM_BUCKET_USDC=${SUM_BUCKET_USDC.toFixed(2)}`,
                  expected: null,
          actual: { sumBucketUsdc: SUM_BUCKET_USDC, ...(txHash ? { txhash: txHash } : {}) },
                  timestamp: Date.now(),
                });
              }
            }
          } catch (e: any) {
            addResult(section, { success: false, message: "Transfer from bank to bucket failed", error: e?.message, timestamp: Date.now() });
          }
        }
      } else if (SUM_BUCKET_USDC >= WORKING_CAPITAL) {
        addResult(section, { success: true, message: "Step 4: SUM_BUCKET_USDC >= WORKING_CAPITAL — proceed without transfer.", timestamp: Date.now() });
      }

      const shouldProceedTrading = SUM_BUCKET_USDC >= WORKING_CAPITAL || bucketRows.length > 0;
      if (!shouldProceedTrading) {
        addResult(section, { success: true, message: "FiClient tests completed (no trading)", expected: null, actual: { stoppedAt: "noTrading" }, timestamp: Date.now() });
      } else {
        // Step 5: Market selection — DEFAULT_SYMBOL from defaultMarket.symbol
        const DEFAULT_SYMBOL = defaultMarket?.symbol ?? marketIdString;
        addResult(section, {
          success: true,
          message: "Step 5: Market selection — default market index, DEFAULT_SYMBOL for orders",
          expected: null,
          actual: { defaultSymbol: DEFAULT_SYMBOL },
          timestamp: Date.now(),
        });

        let CURRENT_PRICE = 0;
        try {
          const marketData = await infoClient.getMarketData(marketIdString);
          const raw = marketData as any;
          const lp = raw.lastPrice ?? raw.last_price ?? raw.markPrice ?? raw.mark_price;
          // CRITICAL: Orderbook prices from getMarketData() are in satoshi format (e.g., "6500000000000" = $65,000).
          // Must convert to decimal before using as CURRENT_PRICE for quantity/price grid calculations.
          CURRENT_PRICE = lp ? fromSatoshi(String(lp)) : 0;
          if (CURRENT_PRICE <= 0) CURRENT_PRICE = 50000;
        } catch {
          CURRENT_PRICE = 50000;
        }

        // Step 6: Position and risk assessment — per-bucket long/short counts, aggregate ratio, health per bucket
        let aggregateLongCount = 0;
        let aggregateShortCount = 0;
        const positions = await infoClient.getPositions(walletAddr).catch(() => []);
        const posList = Array.isArray(positions) ? positions : [];
        for (const p of posList) {
          const side = ((p as any).side ?? (p as any).positionSide ?? "").toUpperCase();
          if (side.includes("BUY") || side.includes("LONG")) aggregateLongCount++;
          else aggregateShortCount++;
        }
        const aggregateRatio =
          aggregateShortCount > 0
            ? aggregateLongCount / aggregateShortCount
            : aggregateLongCount > 0
              ? 999
              : 1;
        addResult(section, {
          success: true,
          message: `Step 6: Position analysis — aggregate longs: ${aggregateLongCount}, shorts: ${aggregateShortCount}, ratio: ${aggregateRatio.toFixed(4)}`,
          expected: null,
          actual: { aggregateLongCount, aggregateShortCount, aggregateRatio },
          timestamp: Date.now(),
        });

        let minHealthRate = 2;
        let healthWarning = false;
        for (const row of bucketRows) {
          try {
            const bucketResp = await infoClient.getBucket(row.bucketId);
            const equity = fromSatoshi(bucketResp.totalEquity ?? "0");
            const used = fromSatoshi(bucketResp.usedMargin ?? bucketResp.depositedMargin ?? "1");
            const hr = used > 0 ? equity / used : 2;
            if (hr < minHealthRate) minHealthRate = hr;
            if (hr < HEALTH_RATE_SAFE) healthWarning = true;
          } catch (_) {}
        }
        addResult(section, {
          success: minHealthRate >= HEALTH_RATE_DANGER,
          message: `Step 6: Risk — min health rate ${minHealthRate.toFixed(2)} (danger <${HEALTH_RATE_DANGER}, warn <${HEALTH_RATE_SAFE})`,
          expected: null,
          actual: { minHealthRate, healthWarning },
          timestamp: Date.now(),
        });
        if (minHealthRate < HEALTH_RATE_DANGER) {
          addResult(section, { success: false, message: "Liquidation risk detected in bucket. Stop testing flow.", timestamp: Date.now() });
        } else {
          if (healthWarning) {
            addResult(section, { success: true, message: "Health rate in warning zone.", timestamp: Date.now() });
          }

          const numBuckets = Math.max(bucketRows.length, 1);
          const baseAmount = WORKING_CAPITAL / (numBuckets * 15);
          const longMultipliers = [1, 1.5, 2, 3, 4];
          const longAmounts = longMultipliers.map((m) => (baseAmount * m) / CURRENT_PRICE);
          const shortAmounts = longMultipliers.map((m) => (baseAmount * m) / CURRENT_PRICE);
          const decrement = (CURRENT_PRICE * 0.5) / 4;
          const increment = (CURRENT_PRICE * 0.5) / 4;
          const longPrices = [1, 2, 3, 4, 5].map((i) => CURRENT_PRICE - i * decrement);
          const shortPrices = [1, 2, 3, 4, 5].map((i) => CURRENT_PRICE + i * increment);
          const sumLongAmounts = longAmounts.reduce((a, b) => a + b, 0);
          const leverageStr = toSatoshi(String(DEFAULT_LEVERAGE));
          const placedLimitOrderIds: string[] = [];
          const placeOrderTxHashes: string[] = [];
          const totalGridOrders = bucketRows.length * (longPrices.length + shortPrices.length);
          let gridOrderProgress = 0;

          // Step 7: Place limit orders for longs (buy grid below current price)
          addResult(section, { success: true, message: `Step 7: Place limit orders for longs (5 levels below current price) (0/${totalGridOrders})`, timestamp: Date.now() });
          for (const row of bucketRows) {
            if (row.usdc < sumLongAmounts * CURRENT_PRICE) {
              addResult(section, { success: true, message: `Skip bucket ${row.bucketId.substring(0, 12)}... — insufficient balance`, timestamp: Date.now() });
              continue;
            }
            for (let i = 0; i < longPrices.length; i++) {
              try {
                const result = await exchangeClient.placeOrder({
                  marketId: marketIdString,
                  bucketId: row.bucketId,
                  side: "buy",
                  orderType: "LIMIT",
                  price: longPrices[i],
                  quantity: Math.min(longAmounts[i], 0.01),
                  timeInForce: "GTC",
                  leverage: leverageStr,
                });
                const accepted = result?.success === true;
                const txHash = result?.txhash ?? "";
                if (result?.orderId) placedLimitOrderIds.push(result.orderId);
                if (txHash) placeOrderTxHashes.push(txHash);
                gridOrderProgress += 1;
                addResult(section, { success: true, message: `Long order placed (${gridOrderProgress}/${totalGridOrders})`, timestamp: Date.now() });
                if (!accepted) {
                  const errMsg = (result as any)?.errorMessage ?? (result as any)?.message ?? "";
                  addResult(section, { success: false, message: "Place order (long) rejected", expected: "order accepted", actual: { error: errMsg, txhash: txHash || undefined }, raw_data: result, timestamp: Date.now() });
                }
              } catch (_) {}
            }
          }

          // Step 8: Place limit orders for shorts (sell grid above current price)
          addResult(section, { success: true, message: `Step 8: Place limit orders for shorts (5 levels above current price) (${gridOrderProgress}/${totalGridOrders})`, timestamp: Date.now() });
          for (const row of bucketRows) {
            try {
              const bucketResp = await infoClient.getBucket(row.bucketId);
              const am = fromSatoshi(bucketResp.availableMargin ?? "0");
              if (am < sumLongAmounts * CURRENT_PRICE) {
                addResult(section, { success: true, message: `Skip bucket for shorts — insufficient collateral`, timestamp: Date.now() });
                continue;
              }
              for (let i = 0; i < shortPrices.length; i++) {
                try {
                  const result = await exchangeClient.placeOrder({
                    marketId: marketIdString,
                    bucketId: row.bucketId,
                    side: "sell",
                    orderType: "LIMIT",
                    price: shortPrices[i],
                    quantity: Math.min(shortAmounts[i], 0.01),
                    timeInForce: "GTC",
                    leverage: leverageStr,
                  });
                  const accepted = result?.success === true;
                  const txHash = result?.txhash ?? "";
                  if (result?.orderId) placedLimitOrderIds.push(result.orderId);
                  if (txHash) placeOrderTxHashes.push(txHash);
                  gridOrderProgress += 1;
                  addResult(section, { success: true, message: `Short order placed (${gridOrderProgress}/${totalGridOrders})`, timestamp: Date.now() });
                  if (!accepted) {
                    const errMsg = (result as any)?.errorMessage ?? (result as any)?.message ?? "";
                    addResult(section, { success: false, message: "Place order (short) rejected", expected: "order accepted", actual: { error: errMsg, txhash: txHash || undefined }, raw_data: result, timestamp: Date.now() });
                  }
                } catch (_) {}
              }
            } catch (_) {}
          }
          addResult(section, {
            success: true,
            message: `Placed ${placedLimitOrderIds.length} limit orders (longs + shorts); tx hashes: ${placeOrderTxHashes.length}`,
            expected: null,
          actual: { placedLimitOrderIds: placedLimitOrderIds.length, txHashCount: placeOrderTxHashes.length, txHashes: placeOrderTxHashes.length ? placeOrderTxHashes : undefined },
            timestamp: Date.now(),
          });

          // Recheck health after limits; cancel new orders if dangerous
          let hrAfterLimits = minHealthRate;
          for (const row of bucketRows) {
            try {
              const bucketResp = await infoClient.getBucket(row.bucketId);
              const equity = fromSatoshi(bucketResp.totalEquity ?? "0");
              const used = fromSatoshi(bucketResp.usedMargin ?? "1");
              const hr = used > 0 ? equity / used : 2;
              if (hr < hrAfterLimits) hrAfterLimits = hr;
            } catch (_) {}
          }
          const step8Reverted = hrAfterLimits < HEALTH_RATE_REVERT;
          if (step8Reverted && exchangeClient?.cancelOrder) {
            for (const oid of placedLimitOrderIds) {
              try {
                await exchangeClient.cancelOrder({ orderId: oid, marketId: marketIdString });
              } catch (_) {}
            }
            addResult(section, { success: false, message: "Reverted market orders due to health risk (cancelled new limit orders).", timestamp: Date.now() });
          }

          // Step 9: Continuation — open positions with market orders (imbalance hedge).
          // Use fresh bucket available margin (post Steps 6/7) so balance check is accurate.
          if (!step8Reverted && bucketRows.length > 0 && CURRENT_PRICE > 0) {
            for (const row of bucketRows) {
              try {
                const bucketResp = await infoClient.getBucket(row.bucketId);
                row.usdc = fromSatoshi(bucketResp.availableMargin ?? "0");
              } catch (_) {}
            }
            const hedgeBucket = bucketRows.reduce((a, b) => (a.usdc >= b.usdc ? a : b));
            if (hedgeBucket.usdc <= 0) {
              addResult(section, { success: true, message: "Step 9: No bucket with available margin; skip hedge.", timestamp: Date.now() });
            } else if (aggregateLongCount > aggregateShortCount * IMBALANCE_THRESHOLD) {
              const hedgeAmount = ((aggregateLongCount - aggregateShortCount) * baseAmount) / 2 / CURRENT_PRICE;
              const notional = hedgeAmount * CURRENT_PRICE;
              const quantity = Math.min(hedgeAmount, 0.01);
              if (quantity <= 0) {
                addResult(section, { success: true, message: "Step 9: Hedge size would be zero; skip market short.", timestamp: Date.now() });
              } else if (hedgeBucket.usdc < notional) {
                addResult(section, { success: true, message: "Step 9: Skipped market short — insufficient bucket balance (post limit orders).", expected: null, actual: { hedgeBucketUsdc: hedgeBucket.usdc, requiredNotional: notional }, timestamp: Date.now() });
              } else {
                try {
                  const result = await exchangeClient.placeOrder({
                    marketId: marketIdString,
                    bucketId: hedgeBucket.bucketId,
                    side: "sell",
                    orderType: "MARKET",
                    price: CURRENT_PRICE,
                    quantity,
                    timeInForce: "GTC",
                    leverage: leverageStr,
                  });
                  const accepted = result?.success === true;
                  const txHash = result?.txhash ?? "";
                  addResult(section, {
                    success: accepted,
                    message: accepted ? `Step 9: Market short opened to balance ratio.${txHash ? ` txhash: ${txHash}` : ""}` : "Step 9: Market short rejected",
                    expected: "accepted with txhash or error",
                    actual: accepted && txHash ? { txhash: txHash } : { txhash: txHash || undefined, error: (result as any)?.errorMessage ?? (result as any)?.message ?? "" },
                    raw_data: !accepted && result ? result : undefined,
                    timestamp: Date.now(),
                  });
                } catch (e: any) {
                  addResult(section, { success: false, message: "Market short failed", error: e?.message, timestamp: Date.now() });
                }
              }
            } else if (aggregateShortCount > aggregateLongCount * IMBALANCE_THRESHOLD) {
              const hedgeAmount = ((aggregateShortCount - aggregateLongCount) * baseAmount) / 2 / CURRENT_PRICE;
              const notional = hedgeAmount * CURRENT_PRICE;
              const quantity = Math.min(hedgeAmount, 0.01);
              if (quantity <= 0) {
                addResult(section, { success: true, message: "Step 9: Hedge size would be zero; skip market long.", timestamp: Date.now() });
              } else if (hedgeBucket.usdc < notional) {
                addResult(section, { success: true, message: "Step 9: Skipped market long — insufficient bucket balance (post limit orders).", expected: null, actual: { hedgeBucketUsdc: hedgeBucket.usdc, requiredNotional: notional }, timestamp: Date.now() });
              } else {
                try {
                  const result = await exchangeClient.placeOrder({
                    marketId: marketIdString,
                    bucketId: hedgeBucket.bucketId,
                    side: "buy",
                    orderType: "MARKET",
                    price: CURRENT_PRICE,
                    quantity,
                    timeInForce: "GTC",
                    leverage: leverageStr,
                  });
                  const accepted = result?.success === true;
                  const txHash = result?.txhash ?? "";
                  addResult(section, {
                    success: accepted,
                    message: accepted ? `Step 9: Market long opened to balance ratio.${txHash ? ` txhash: ${txHash}` : ""}` : "Step 9: Market long rejected",
                    expected: "accepted with txhash or error",
                    actual: accepted && txHash ? { txhash: txHash } : { txhash: txHash || undefined, error: (result as any)?.errorMessage ?? (result as any)?.message ?? "" },
                    raw_data: !accepted && result ? result : undefined,
                    timestamp: Date.now(),
                  });
                } catch (e: any) {
                  addResult(section, { success: false, message: "Market long failed", error: e?.message, timestamp: Date.now() });
                }
              }
            } else {
              addResult(section, { success: true, message: "Step 9: Positions balanced; no market orders needed.", timestamp: Date.now() });
            }
          }
        }

        // Step 10: End of testing flow — final balances, positions, ratios
        let finalLongCount = 0;
        let finalShortCount = 0;
        try {
          const posList2 = await infoClient.getPositions(walletAddr);
          const list = Array.isArray(posList2) ? posList2 : [];
          for (const p of list) {
            const side = ((p as any).side ?? "").toUpperCase();
            if (side.includes("BUY") || side.includes("LONG")) finalLongCount++;
            else finalShortCount++;
          }
        } catch (_) {}
        let finalSumBucket = SUM_BUCKET_USDC;
        try {
          const blist = await infoClient.getBuckets(walletAddr).catch(() => []);
          const arr: RawBucket[] = Array.isArray(blist) ? blist : [];
          finalSumBucket = 0;
          for (const b of arr) {
            const bid = b.bucketId ?? "";
            if (!bid) continue;
            const br = await infoClient.getBucket(bid).catch(() => null);
            if (br) {
              finalSumBucket += fromSatoshi(br.availableMargin ?? "0");
            }
          }
        } catch (_) {}
        const finalRatio = finalShortCount > 0 ? finalLongCount / finalShortCount : finalLongCount > 0 ? 999 : 1;
        addResult(section, {
          success: true,
          message: "Step 10: End of testing flow — Testing flow completed with positions opened.",
          expected: null,
          actual: {
            finalBalances: finalSumBucket,
            finalLongCount,
            finalShortCount,
            finalRatio,
          },
          timestamp: Date.now(),
        });
      }

      addResult(section, {
        success: true,
        message: "FiClient tests completed",
        expected: null,
        actual: {
          walletAddress: walletAddr,
          marketId: marketIdString,
          flow: "tradingAutomation.md (Step 1: refresh nonce, then 9 steps)",
          methodsTested: [
            "balanceChecks",
            "fundSufficiencyAndTransfer",
            "marketSelection",
            "positionAndRiskAssessment",
            "limitOrdersLongs",
            "limitOrdersShorts",
            "marketOrdersContinuation",
            "placeOrder",
            "cancelOrder",
            "extractToBucket",
          ],
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "FiClient test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // ---------------------------------------------------------------------------
  // FiClient Order Types & Time-In-Force Tests
  // ---------------------------------------------------------------------------
  // Mirrors orchestrator E2E tests: market orders, IOC, FOK, DAY, PostOnly,
  // ReduceOnly, TP/SL, leverage, and hidden orders.
  // ---------------------------------------------------------------------------
  const testFiClientOrders = async () => {
    const section = "FiClient Orders";
    if (!mnemonic) {
      addResult(section, { success: false, message: "Mnemonic required", error: "No mnemonic provided", timestamp: Date.now() });
      return;
    }
    if (!exchangeClient || !exchangeReady) {
      addResult(section, { success: false, message: "FiClient not ready", error: "Set mnemonic and base URL, then wait for FiClient to initialize.", timestamp: Date.now() });
      return;
    }

    // Resolve market ID (reuse logic from testFiClient)
    let currentMarketId = marketIndex || effectiveMarketId || getFirstMarketId(fetchedMarketsRef.current) || "";
    if (!currentMarketId || currentMarketId.trim() === "") {
      if (fetchedMarketsRef.current && fetchedMarketsRef.current.length > 0) {
        const firstMarket = fetchedMarketsRef.current.find((m: Market) => m.active) ?? fetchedMarketsRef.current[0];
        if (firstMarket) currentMarketId = firstMarket.index ?? firstMarket.marketIndex ?? "";
      } else if (markets && markets.length > 0) {
        const firstMarket = markets.find((m: Market) => m.active) ?? markets[0];
        if (firstMarket) currentMarketId = firstMarket.index ?? firstMarket.marketIndex ?? "";
      }
      if (!currentMarketId || currentMarketId.trim() === "") {
        try {
          const marketsData = await fetchMarkets();
          const list = Array.isArray(marketsData) ? marketsData : [];
          fetchedMarketsRef.current = list;
          const firstMarket = list.find((m: Market) => m.active) ?? list[0];
          if (firstMarket) currentMarketId = firstMarket.index ?? firstMarket.marketIndex ?? "";
        } catch { /* ignore */ }
      }
    }
    if (!currentMarketId || currentMarketId.trim() === "") {
      addResult(section, { success: false, message: "Market ID required", error: "No market available", timestamp: Date.now() });
      return;
    }

    const walletAddr = exchangeClient.getWalletAddress();
    const currentWalletAddress = walletAddress || "";
    if (!currentWalletAddress || walletAddr.toLowerCase() !== currentWalletAddress.toLowerCase()) {
      addResult(section, { success: false, message: "Wallet address mismatch or missing", error: `FiClient=${walletAddr}, Page=${currentWalletAddress}`, timestamp: Date.now() });
      return;
    }

    // Resolve bucket
    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });
    }
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }
    const infoClient = infoClientRef.current;

    let bucketId = "";
    try {
      const buckets = await infoClient.getBuckets(walletAddr).catch(() => []);
      const bucketsList: RawBucket[] = Array.isArray(buckets) ? buckets : [];
      for (const b of bucketsList) {
        const bid = b.bucketId ?? "";
        if (!bid) continue;
        const br = await infoClient.getBucket(bid).catch(() => null);
        if (br && parseFloat(br.availableMargin ?? "0") > 0) {
          bucketId = bid;
          break;
        }
      }
    } catch { /* ignore */ }
    if (!bucketId) {
      addResult(section, { success: false, message: "No funded bucket available", error: "Need at least one bucket with available margin", timestamp: Date.now() });
      return;
    }

    // Refresh nonce before tests
    try { await exchangeClient.refreshNonceFromServer(); } catch { /* ignore */ }

    // Get current price for price calculations
    let currentPrice = 0;
    try {
      const md = await infoClient.getMarketData(currentMarketId);
      const raw = md as any;
      const lp = raw.lastPrice ?? raw.last_price ?? raw.markPrice ?? raw.mark_price;
      currentPrice = lp ? fromSatoshi(String(lp)) : 0;
    } catch { /* ignore */ }
    if (currentPrice <= 0) currentPrice = 50000;

    const marketIdString = currentMarketId;
    const leverageStr = toSatoshi("10");
    const smallQty = 0.001; // Small quantity for tests (satoshis handled by SDK)

    addResult(section, {
      success: true,
      message: `Setup: market=${marketIdString}, bucket=${bucketId.substring(0, 16)}..., price=${currentPrice}, qty=${smallQty}`,
      actual: { marketId: marketIdString, bucketId, currentPrice, quantity: smallQty },
      timestamp: Date.now(),
    });

    // Helper: place an order and report result
    const placeAndReport = async (
      testName: string,
      params: Parameters<typeof exchangeClient.placeOrder>[0],
    ): Promise<{ success: boolean; txhash?: string; errorMessage?: string }> => {
      try {
        const result = await exchangeClient.placeOrder(params);
        const accepted = result?.success === true;
        const txHash = result?.txhash ?? "";
        const errorMessage = accepted ? "" : ((result as any)?.errorMessage ?? (result as any)?.message ?? "");
        addResult(section, {
          success: accepted,
          message: accepted
            ? `✅ ${testName}: accepted${txHash ? ` (tx: ${txHash.substring(0, 16)}...)` : ""}`
            : `❌ ${testName}: rejected — ${errorMessage}`,
          actual: {
            accepted,
            txhash: txHash || undefined,
            ...(!accepted && { error: errorMessage }),
          },
          raw_data: !accepted && result ? result : undefined,
          timestamp: Date.now(),
        });
        // Small delay between tests for nonce propagation
        await new Promise((r) => setTimeout(r, 300));
        return { success: accepted, txhash: txHash, errorMessage };
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: `❌ ${testName}: exception — ${e.message}`,
          error: e.message,
          timestamp: Date.now(),
        });
        return { success: false, errorMessage: e.message };
      }
    };

    // Non-crossing prices for limit orders that should rest on book
    const bidPrice = currentPrice * 0.90; // 10% below — should not cross
    const askPrice = currentPrice * 1.10; // 10% above — should not cross

    try {
      // ---- Market Orders ----
      addResult(section, { success: true, message: "── Market Orders ──", timestamp: Date.now() });

      await placeAndReport("Market Buy Order", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "MARKET",
        price: 0, // Market orders don't require a price
        quantity: smallQty,
        timeInForce: "GTC",
        leverage: leverageStr,
      });

      await placeAndReport("Market Sell Order", {
        marketId: marketIdString,
        bucketId,
        side: "sell",
        orderType: "MARKET",
        price: 0,
        quantity: smallQty,
        timeInForce: "GTC",
        leverage: leverageStr,
      });

      // ---- Time In Force: IOC ----
      addResult(section, { success: true, message: "── Time In Force: IOC ──", timestamp: Date.now() });

      await placeAndReport("Limit Buy IOC", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "IOC",
        leverage: leverageStr,
      });

      // ---- Time In Force: FOK ----
      addResult(section, { success: true, message: "── Time In Force: FOK ──", timestamp: Date.now() });

      await placeAndReport("Limit Buy FOK", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "FOK",
        leverage: leverageStr,
      });

      // ---- Time In Force: DAY ----
      addResult(section, { success: true, message: "── Time In Force: DAY ──", timestamp: Date.now() });

      await placeAndReport("Limit Buy DAY", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "DAY",
        leverage: leverageStr,
      });

      // ---- Post-Only ----
      addResult(section, { success: true, message: "── Post-Only ──", timestamp: Date.now() });

      await placeAndReport("Limit Buy Post-Only (non-crossing)", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        postOnly: true,
        leverage: leverageStr,
      });

      // ---- Reduce-Only ----
      addResult(section, { success: true, message: "── Reduce-Only ──", timestamp: Date.now() });

      await placeAndReport("Limit Buy Reduce-Only", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        reduceOnly: true,
        leverage: leverageStr,
      });

      // ---- Take Profit / Stop Loss ----
      addResult(section, { success: true, message: "── TP/SL ──", timestamp: Date.now() });

      const tpPrice = toSatoshi(currentPrice * 1.10); // 10% above
      const slPrice = toSatoshi(currentPrice * 0.95); // 5% below
      await placeAndReport("Limit Buy with TP/SL", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        leverage: leverageStr,
        takeProfit: tpPrice,
        stopLoss: slPrice,
      });

      // ---- Leverage ----
      addResult(section, { success: true, message: "── Leverage ──", timestamp: Date.now() });

      const leverageX5 = toSatoshi("5");
      await placeAndReport("Limit Buy with 5x Leverage", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        leverage: leverageX5,
      });

      // ---- Hidden / Iceberg ----
      addResult(section, { success: true, message: "── Hidden / Iceberg ──", timestamp: Date.now() });

      await placeAndReport("Hidden Limit Buy", {
        marketId: marketIdString,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: bidPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        hidden: true,
        leverage: leverageStr,
      });

      // ---- Market Order Crossing with Resting Limit ----
      addResult(section, { success: true, message: "── Market Crossing Resting Limit ──", timestamp: Date.now() });

      // Place a resting limit sell at ask, then market buy to cross it
      const restingResult = await placeAndReport("Resting Limit Sell (setup)", {
        marketId: marketIdString,
        bucketId,
        side: "sell",
        orderType: "LIMIT",
        price: askPrice,
        quantity: smallQty,
        timeInForce: "GTC",
        leverage: leverageStr,
      });
      if (restingResult.success) {
        await new Promise((r) => setTimeout(r, 1000)); // Wait for orderbook update
        await placeAndReport("Market Buy crosses Resting Sell", {
          marketId: marketIdString,
          bucketId,
          side: "buy",
          orderType: "MARKET",
          price: 0,
          quantity: smallQty,
          timeInForce: "GTC",
          leverage: leverageStr,
        });
      }

      addResult(section, {
        success: true,
        message: "FiClient Order Types & TIF tests completed",
        actual: { testsRun: "market, IOC, FOK, DAY, postOnly, reduceOnly, TP/SL, leverage, hidden, crossing" },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "FiClient Order tests error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Bucket Equity Test
  // ---------------------------------------------------------------------------
  // Tests bucket creation, bank→bucket, bucket→bucket, bucket→bank, USDC travel,
  // and bank transfer (spot wallet → external address).
  // ---------------------------------------------------------------------------
  const testBucketEquity = async () => {
    const section = "Bucket Equity Test";
    if (!mnemonic || !mnemonic.trim()) {
      addResult(section, {
        success: false,
        message: "Mnemonic required",
        error: "No mnemonic provided",
        timestamp: Date.now(),
      });
      return;
    }
    if (!exchangeClient || !exchangeReady) {
      addResult(section, {
        success: false,
        message: "FiClient not ready",
        error: "Set mnemonic and base URL, then wait for FiClient to initialize.",
        timestamp: Date.now(),
      });
      return;
    }
    const walletAddr = walletAddress || exchangeClient.getWalletAddress();
    if (!walletAddr || !walletAddr.trim()) {
      addResult(section, {
        success: false,
        message: "Wallet address required",
        error: "Wallet address not available",
        timestamp: Date.now(),
      });
      return;
    }

    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });
    }
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }
    const infoClient = infoClientRef.current;

    const testAmount = Math.min(
      BUCKET_EQUITY_TEST_AMOUNT_USDC,
      BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC,
    );
    const bankTransferAmount = Math.min(
      BUCKET_EQUITY_TEST_BANK_TRANSFER_AMOUNT_USDC,
      BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC,
    );

    const toNumFromBal = (v: unknown): number =>
      typeof v === "number" && !Number.isNaN(v) ? v : typeof v === "string" ? parseFloat(v) || 0 : 0;

    try {
      addResult(section, {
        success: true,
        message: `Starting Bucket Equity Test (amount: ${testAmount} USDC, bank transfer: ${bankTransferAmount} USDC)`,
        expected: null,
        actual: { testAmount, bankTransferAmount, toAddress: BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS },
        timestamp: Date.now(),
      });

      // 1. Create bucket A (always create new)
      addResult(section, {
        success: true,
        message: "Step 1: Create bucket A (USDC cross margin)",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      let bucketAId: string | null = null;
      try {
        const createRes = await exchangeClient.createBucket({
          collateralAssetIndex: 1,
          marginType: "cross",
          initialMargin: "0",
        });
        bucketAId = (createRes as { bucketId?: string }).bucketId ?? null;
        const createAErr = !createRes?.success ? ((createRes as any)?.errorMessage ?? (createRes as any)?.message ?? "") : "";
        addResult(section, {
          success: !!createRes?.success,
          message: `Create bucket A: ${createRes?.success ? "OK" : "Failed"}`,
          expected: "success",
          actual: { bucketId: bucketAId, txhash: (createRes as { txhash?: string })?.txhash, ...(!createRes?.success && { error: createAErr }) },
          raw_data: !createRes?.success && createRes ? createRes : undefined,
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: "Create bucket A failed",
          error: e?.message,
          timestamp: Date.now(),
        });
      }

      // 2. Create bucket B (always create new)
      addResult(section, {
        success: true,
        message: "Step 2: Create bucket B (USDC cross margin)",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      let bucketBId: string | null = null;
      try {
        const createResB = await exchangeClient.createBucket({
          collateralAssetIndex: 1,
          marginType: "cross",
          initialMargin: "0",
        });
        bucketBId = (createResB as { bucketId?: string }).bucketId ?? null;
        const createBErr = !createResB?.success ? ((createResB as any)?.errorMessage ?? (createResB as any)?.message ?? "") : "";
        addResult(section, {
          success: !!createResB?.success,
          message: `Create bucket B: ${createResB?.success ? "OK" : "Failed"}`,
          expected: "success",
          actual: { bucketId: bucketBId, txhash: (createResB as { txhash?: string })?.txhash, ...(!createResB?.success && { error: createBErr }) },
          raw_data: !createResB?.success && createResB ? createResB : undefined,
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: "Create bucket B failed",
          error: e?.message,
          timestamp: Date.now(),
        });
      }

      const sourceBucketId = bucketAId || bucketBId;
      if (!sourceBucketId) {
        addResult(section, {
          success: false,
          message: "No bucket created; cannot proceed with transfers",
          expected: "at least one bucket",
          actual: null,
          timestamp: Date.now(),
        });
        return;
      }

      // 3. Bank → bucket (extractToBucket)
      addResult(section, {
        success: true,
        message: `Step 3: Bank → bucket (${testAmount} USDC)`,
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      let bankUsdcBefore = 0;
      try {
        const bal = await infoClient.getBalanceDecimal(walletAddr, "USDC");
        const b = bal as Record<string, unknown>;
        bankUsdcBefore = toNumFromBal(b?.available) || toNumFromBal(b?.balance) || 0;
      } catch { /* ignore */ }
      const effectiveAmount = Math.min(testAmount, bankUsdcBefore, BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC);
      if (effectiveAmount < 10) {
        addResult(section, {
          success: false,
          message: "Insufficient bank USDC for bank→bucket (need at least 10)",
          expected: "bank USDC >= 10",
          actual: { bankUsdc: bankUsdcBefore },
          timestamp: Date.now(),
        });
        addResult(section, {
          success: true,
          message: "Bucket Equity Test completed (stopped: insufficient funds)",
          expected: null,
          actual: { stoppedAt: "bankToBucket" },
          timestamp: Date.now(),
        });
        return;
      }
      try {
        const extractRes = await exchangeClient.extractToBucket({
          bucketId: sourceBucketId,
          assetId: "USDC",
          amount: effectiveAmount,
        });
        const extractSuccess = !!extractRes?.success;
        const extractErr = !extractSuccess ? ((extractRes as any)?.errorMessage ?? (extractRes as any)?.message ?? "") : "";
        addResult(section, {
          success: extractSuccess,
          message: `Bank → bucket: ${extractSuccess ? "OK" : "Failed"}`,
          expected: "success",
          actual: {
            amount: effectiveAmount,
            txhash: (extractRes as { txhash?: string })?.txhash,
            ...(!extractSuccess && { error: extractErr }),
          },
          raw_data: !extractSuccess && extractRes ? extractRes : undefined,
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: "Bank → bucket failed",
          error: e?.message,
          timestamp: Date.now(),
        });
      }

      // 4. Bucket → bucket (if we have both buckets)
      if (bucketAId && bucketBId) {
        addResult(section, {
          success: true,
          message: "Step 4: Bucket A → Bucket B (bucket transfer)",
          expected: null,
          actual: null,
          timestamp: Date.now(),
        });
        const halfAmount = Math.floor(effectiveAmount / 2);
        if (halfAmount >= 10) {
          try {
            const transferRes = await exchangeClient.bucketTransfer({
              fromBucketId: bucketAId,
              toBucketId: bucketBId,
              amount: halfAmount,
            });
            const transferSuccess = !!transferRes?.success;
            const transferErr = !transferSuccess ? ((transferRes as any)?.errorMessage ?? (transferRes as any)?.message ?? "") : "";
            addResult(section, {
              success: transferSuccess,
              message: `Bucket A → B: ${transferSuccess ? "OK" : "Failed"}`,
              expected: "success",
              actual: {
                amount: halfAmount,
                txhash: (transferRes as { txhash?: string })?.txhash,
                ...(!transferSuccess && { error: transferErr }),
              },
              raw_data: !transferSuccess && transferRes ? transferRes : undefined,
              timestamp: Date.now(),
            });
          } catch (e: any) {
            addResult(section, {
              success: false,
              message: "Bucket → bucket failed",
              error: e?.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message: "Bucket → bucket skipped (amount < 10)",
            expected: null,
            actual: { halfAmount },
            timestamp: Date.now(),
          });
        }
      }

      // 5. Bucket → bank (withdrawFromBucket)
      addResult(section, {
        success: true,
        message: "Step 5: Bucket → bank (withdraw)",
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      const withdrawAmount = Math.min(10, effectiveAmount);
      try {
        const withdrawRes = await exchangeClient.withdrawFromBucket({
          bucketId: sourceBucketId,
          assetId: "USDC",
          amount: withdrawAmount,
        });
        const withdrawSuccess = !!withdrawRes?.success;
        const withdrawErr = !withdrawSuccess ? ((withdrawRes as any)?.errorMessage ?? (withdrawRes as any)?.message ?? "") : "";
        addResult(section, {
          success: withdrawSuccess,
          message: `Bucket → bank: ${withdrawSuccess ? "OK" : "Failed"}`,
          expected: "success",
          actual: {
            amount: withdrawAmount,
            txhash: (withdrawRes as { txhash?: string })?.txhash,
            ...(!withdrawSuccess && { error: withdrawErr }),
          },
          raw_data: !withdrawSuccess && withdrawRes ? withdrawRes : undefined,
          timestamp: Date.now(),
        });
      } catch (e: any) {
        addResult(section, {
          success: false,
          message: "Bucket → bank failed",
          error: e?.message,
          timestamp: Date.now(),
        });
      }

      // 6. Bank transfer (spot wallet → external address)
      addResult(section, {
        success: true,
        message: `Step 6: Bank transfer (${bankTransferAmount} USDC to ${BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS})`,
        expected: null,
        actual: null,
        timestamp: Date.now(),
      });
      let bankUsdcForTransfer = 0;
      try {
        const bal = await infoClient.getBalanceDecimal(walletAddr, "USDC");
        const b = bal as Record<string, unknown>;
        bankUsdcForTransfer = toNumFromBal(b?.available) || toNumFromBal(b?.balance) || 0;
      } catch { /* ignore */ }
      const effectiveBankTransfer = Math.min(bankTransferAmount, bankUsdcForTransfer, BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC);
      if (effectiveBankTransfer < 0.01) {
        addResult(section, {
          success: false,
          message: "Insufficient bank USDC for external transfer (need at least 0.01)",
          expected: "bank USDC >= 0.01",
          actual: { bankUsdc: bankUsdcForTransfer },
          timestamp: Date.now(),
        });
      } else {
        try {
          const bankRes = await exchangeClient.bankTransfer({
            to: BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS,
            assetId: "USDC",
            amount: effectiveBankTransfer,
          });
          const bankSuccess = !!bankRes?.success;
          const bankErr = !bankSuccess ? ((bankRes as any)?.errorMessage ?? (bankRes as any)?.message ?? "") : "";
          addResult(section, {
            success: bankSuccess,
            message: `Bank transfer to wallet: ${bankSuccess ? "OK" : "Failed"}`,
            expected: "success",
            actual: {
              amount: effectiveBankTransfer,
              to: BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS,
              txhash: (bankRes as { txhash?: string })?.txhash,
              ...(!bankSuccess && { error: bankErr }),
            },
            raw_data: !bankSuccess && bankRes ? bankRes : undefined,
            timestamp: Date.now(),
          });
        } catch (e: any) {
          addResult(section, {
            success: false,
            message: "Bank transfer failed",
            error: e?.message,
            timestamp: Date.now(),
          });
        }
      }

      addResult(section, {
        success: true,
        message: "Bucket Equity Test completed",
        expected: null,
        actual: { steps: ["create bucket A", "create bucket B", "bank→bucket", "bucket→bucket", "bucket→bank", "bank transfer"] },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Bucket Equity Test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Run all tests
  const runAllTests = async () => {
    // Prevent concurrent test runs
    if (isRunning) {
      return;
    }

    setIsRunning(true);
    clearResults();
    toast.info("Running tests", {
      description: "Starting comprehensive SDK test suite...",
    });

    try {
      // Test utilities (synchronous)
      testUtilities();

      // Test InfoClient
      await testInfoClient();

      // Test Authentication (onboarding)
      await testAuthentication("Authentication");

      // Test Wallet Specific endpoints
      await testWalletSpecific();

      // Test FiClient (if mnemonic provided)
      if (mnemonic) {
        await testFiClient();
        await testFiClientOrders();
        await testBucketEquity();
      }

      // Completion toast will be shown by useEffect when isRunning becomes false
    } catch (error: any) {
      addResult("General", {
        success: false,
        message: "Test suite error",
        error: error.message,
        timestamp: Date.now(),
      });
      toast.error("Test suite error", {
        description:
          error.message || "An unexpected error occurred during testing.",
        duration: 6000,
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="App-content-row">
      <h1>SDK QA</h1>
      <p>Comprehensive test suite for all SDK APIs</p>

      {/* Configuration */}
      <div className="App-section">
        <h2>Configuration</h2>
        <div className="sdk-test-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Market:
              <select
                value={marketIndex || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    setMarketIndex(val); // Store as string
                  }
                }}
                className="sdk-test-config-select"
              >
                {markets.length === 0 ? (
                  <option value="">Loading markets...</option>
                ) : (
                  <>
                    <option value="" disabled>
                      -- Select a market --
                    </option>
                    {markets.map((market) => {
                      const marketIdValue =
                        market.index ?? market.marketIndex ?? "";
                      // Prioritize moniker (from API), then symbol, then fallback
                      const displayName = market.moniker
                        ? market.moniker
                        : market.symbol
                          ? market.symbol
                          : market.baseAsset && market.quoteAsset
                            ? `${market.baseAsset}-${market.quoteAsset}`
                            : `Market ID: ${marketIdValue}`;
                      const isActive = market.active ? " [ACTIVE]" : "";
                      // Show truncated ID to avoid UI overflow

                      return (
                        <option key={marketIdValue} value={marketIdValue}>
                          {displayName} - ID: {marketIdValue}
                          {isActive}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
              {markets.length > 0 && (
                <small
                  className={`sdk-test-config-hint ${
                    marketIndex === defaultMarketId ? "info" : "warning"
                  }`}
                >
                  {marketIndex === defaultMarketId
                    ? `✓ Auto-selected default market (ID: ${defaultMarketId?.substring(0, 16)}...)`
                    : `⚠ Manual selection (ID: ${marketIndex?.substring(0, 16)}...)`}
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Mnemonic (for FiClient):
              <input
                type="text"
                value={mnemonic || ""}
                onChange={(e) => setOnboardMnemonic(e.target.value)}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Session Token (for authenticated InfoClient):
              <input
                type="text"
                value={sessionToken}
                onChange={(e) => setSessionToken(e.target.value)}
                disabled={!!(sessionToken && sessionToken.trim() !== "")}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Auth Token:
              <input
                type="text"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                disabled={
                  !!(onboardAuthToken && onboardAuthToken.trim() !== "")
                }
                placeholder={
                  onboardAuthToken
                    ? "Auto-filled from onboarding"
                    : 'Optional - Use "Onboard" button to generate'
                }
                className="sdk-test-config-input"
              />
              {onboardAuthToken && (
                <small className="sdk-test-config-hint success">
                  ✓ Auto-filled from onboarding
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Wallet Address:
              <input
                type="text"
                value={walletAddress || ""}
                disabled={!!(mnemonic && mnemonic.trim() !== "")}
                onChange={() => {
                  // Wallet address is managed by useOnboard, but allow manual override via setAccountData if needed
                  // For now, just show it as read-only when mnemonic is set
                }}
                placeholder="automatic (from mnemonic)"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div className="sdk-test-nonce-block">
            <label>
              Nonce (current wallet):
              {!walletAddress || !walletAddress.trim() ? (
                <span className="sdk-test-config-hint">Connect or enter wallet to see nonce</span>
              ) : (
                <div className="sdk-test-nonce-fields">
                  <div className="sdk-test-nonce-row">
                    <span className="sdk-test-nonce-label">Onchain next:</span>
                    <span className="sdk-test-nonce-value">
                      {onchainNonceError != null
                        ? `Error: ${onchainNonceError}`
                        : onchainNonce != null
                          ? String(onchainNonce)
                          : "—"}
                    </span>
                  </div>
                  <div className="sdk-test-nonce-row">
                    <span className="sdk-test-nonce-label">Local (confirmed / pending / lastSync):</span>
                    <span className="sdk-test-nonce-value">
                      {localNonceState
                        ? `${localNonceState.confirmed} / [${localNonceState.pending.join(", ") || "—"}] / ${localNonceState.lastSync ? new Date(localNonceState.lastSync).toISOString() : "—"}`
                        : "—"}
                    </span>
                  </div>
                  <div className="sdk-test-nonce-row">
                    <span className="sdk-test-nonce-label">Synced next nonce:</span>
                    <span className="sdk-test-nonce-value">
                      {nonceLoading ? "…" : nonceError ? `Error: ${nonceError}` : syncedNextNonce != null ? String(syncedNextNonce) : "—"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="sdk-test-nonce-refresh"
                    disabled={nonceLoading || !walletAddress}
                    onClick={() => {
                      if (!walletAddress || !baseURL.trim()) return;
                      queryNonce(false).catch(() => {});
                      const client = infoClientRef.current ?? new InfoClient({ baseURL });
                      client.getNonceStatus(walletAddress, false).then((res) => {
                        const raw = (res as any).next_nonce ?? (res as any).nextNonce;
                        const n = typeof raw === "string" ? parseInt(raw, 10) : raw;
                        if (typeof n === "number") setOnchainNonce(n);
                        setOnchainNonceError(null);
                      }).catch((err) => setOnchainNonceError(err?.message ?? "Failed"));
                    }}
                  >
                    {nonceLoading ? "Refreshing…" : "Refresh nonce"}
                  </button>
                </div>
              )}
            </label>
          </div>
          <div>
            <label>
              Device ID:
              <input
                type="text"
                value={deviceID}
                onChange={(e) => setDeviceID(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div className="control-box">
            <RunAllTestsButton isRunning={isRunning} onClick={runAllTests} />
            <OnboardButton
              mnemonic={mnemonic || ""}
              baseURL={baseURL}
              onboardLoading={isAuthTestRunning}
              isRunning={isRunning}
              onOnboard={async (mnemonic, baseURL) => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsAuthTestRunning(true);
                  try {
                    await testAuthentication("Authentication");
                  } catch (error) {
                    console.error("Authentication test error:", error);
                  } finally {
                    setIsAuthTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run authentication flow.",
                  });
                }
              }}
            />
            <UtilityTestButton
              isRunning={isRunning}
              isLoading={isUtilityTestRunning}
              onClick={() => {
                setIsUtilityTestRunning(true);
                try {
                  testUtilities();
                } catch (error) {
                  console.error("Utility test error:", error);
                } finally {
                  // Use setTimeout to ensure UI updates before clearing loading state
                  setTimeout(() => {
                    setIsUtilityTestRunning(false);
                  }, 100);
                }
              }}
            />
            <InfoClientTestButton
              isRunning={isRunning}
              isLoading={isInfoClientTestRunning}
              onClick={async () => {
                setIsInfoClientTestRunning(true);
                try {
                  await testInfoClient();
                } catch (error) {
                  console.error("Info Client test error:", error);
                } finally {
                  setIsInfoClientTestRunning(false);
                }
              }}
            />
            <WalletSpecificTestButton
              isRunning={isRunning}
              isLoading={isWalletSpecificTestRunning}
              onClick={async () => {
                setIsWalletSpecificTestRunning(true);
                try {
                  await testWalletSpecific();
                } catch (error) {
                  console.error("Wallet Specific test error:", error);
                } finally {
                  setIsWalletSpecificTestRunning(false);
                }
              }}
            />
            <FiClientTestButton
              mnemonic={mnemonic || ""}
              isRunning={isRunning}
              isLoading={isFiClientTestRunning}
              onClick={async () => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsFiClientTestRunning(true);
                  try {
                    await testFiClient();
                  } catch (error) {
                    console.error("Exchange Client test error:", error);
                  } finally {
                    setIsFiClientTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run Exchange Client tests.",
                  });
                }
              }}
            />
            <FiClientOrderTestButton
              mnemonic={mnemonic || ""}
              isRunning={isRunning}
              isLoading={isFiClientOrderTestRunning}
              onClick={async () => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsFiClientOrderTestRunning(true);
                  try {
                    await testFiClientOrders();
                  } catch (error) {
                    console.error("Order type test error:", error);
                  } finally {
                    setIsFiClientOrderTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run Order Type & TIF tests.",
                  });
                }
              }}
            />
            <BucketEquityTestButton
              mnemonic={mnemonic || ""}
              isRunning={isRunning}
              isLoading={isBucketEquityTestRunning}
              onClick={async () => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsBucketEquityTestRunning(true);
                  try {
                    await testBucketEquity();
                  } catch (error) {
                    console.error("Bucket Equity test error:", error);
                  } finally {
                    setIsBucketEquityTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run Bucket Equity tests.",
                  });
                }
              }}
            />
            <ClearResultsButton isRunning={isRunning} onClick={clearResults} />
          </div>
        </div>

        {onboardAuthToken && (
          <span className="sdk-test-status success">
            ✓ Auth Token: {onboardAuthToken.substring(0, 20)}...
          </span>
        )}
        {onboardError && (
          <span className="sdk-test-status error">
            ❌ Onboard Error: {onboardError}
            <DismissErrorButton onClick={clearOnboardError} />
          </span>
        )}
      </div>

      {/* Results */}
      <div className="sdk-test-results">
        {Object.entries(sections).map(([sectionName, section]) => {
          const isExpanded = expandedSections.has(sectionName);
          return (
            <div key={sectionName} className="sdk-test-section">
              <SectionHeader
                sectionName={sectionName}
                isExpanded={isExpanded}
                onToggle={() => toggleSection(sectionName)}
              />
              {isExpanded && (
                <div className="sdk-test-section-content">
                  {section.results.map((result, index) => (
                    <TestResultItem key={index} result={result} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(sections).length === 0 && !isRunning && (
        <div className="sdk-test-empty-state">
          Click "Run All Tests" to start testing the SDK APIs
        </div>
      )}
    </div>
  );
}

/**
 * SDK Test Page wrapper. Provides SubscriptionContext so useBuckets (and other hooks)
 * use context instead of a passed subscriptionClient (Option A / Strategy 5).
 */
export default function SDKTestPage() {
  return (
    <SubscriptionProvider defaultWsURL={DEFAULT_WS_URL}>
      <SDKTestPageContent />
    </SubscriptionProvider>
  );
}
